// v6 F11.6a-FINAL-V3 → F15 → F16 — Schrijnwerk module.
// v6 F16 §3 — Object-picker refactor: van brede knoppenrij → één sobere
// "+ Object toevoegen"-knop die een modal opent (bottom sheet op mobiel,
// centered dialog op desktop). Objecten zijn logisch gegroepeerd per
// zone_type. Rustiger op mobiel, sneller scanbaar.
// v6 F15 correcties:
//   - `zone.zone_type` bepaalt of dit een legacy (`joinery`) of nieuwe
//     variant (`interior_joinery` / `exterior_joinery`) is.
//   - Kind-picker toont enkel de kinds die bij die zone_type horen.
//   - Voor v15-zones is de default_side impliciet (interior of exterior);
//     de "Binnen/Buiten/Both" toggle blijft alleen zichtbaar voor legacy.
//   - Template-filter selecteert automatisch tussen basis + decoratief
//     op basis van de nieuwe template_context waarden.
//   - Voorbereiding voor parts-UI (v15.1) — huidige v15 gebruikt nog
//     L × H als input; parts-model draait server-side wanneer objecten
//     `parts=[]` (fallback op oude calc).
import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Check, Trash2, Plus, X, ChevronDown } from "lucide-react";
import RawNumInput from "./RawNumInput";
import { UNIT_LABEL } from "../../lib/calculator";
import { resolveAutoSource, AutoSourcesContext } from "../../lib/autoSource";
import { autoSelectOnFocus } from "../../lib/autoNameSelect";
import { useContext, useRef } from "react";
import { EditableDescription } from "./EditableDescription";

// v6 F15 — Uitgebreide OBJECT_KIND_LABEL met alle nieuwe binnen- en
// buitenschrijnwerk kinds. Legacy kinds (window/door/…) blijven aanwezig
// voor bestaande generieke joinery-zones.
const OBJECT_KIND_LABEL = {
  // Legacy
  window:        "Raam",
  door:          "Deur",
  garage_door:   "Garagepoort",
  gate:          "Poort / hek",
  shutter:       "Luik",
  shutter_box:   "Rolluikkast",
  shutter_slats: "Rolluiklamellen",
  custom_object: "Vrij object",
  // v6 F15 Binnenschrijnwerk
  interior_door:          "Binnendeur",
  cabinet_door:           "Kastdeur",
  interior_sliding_door:  "Schuifdeur",
  interior_window:        "Binnenraam",
  kitchen_front:          "Keukenmeubelfront",
  kitchen_cabinet:        "Keukenmeubel",
  bath_front:             "Badmeubelfront",
  bath_cabinet:           "Badmeubel",
  radiator_cabinet:       "Radiatorkast",
  open_cabinet:           "Open kast",
  closed_cabinet:         "Gesloten kast",
  desk_cabinet:           "Meubel",
  kitchen_tablet:         "Keukentablet",
  mdf_panel:              "MDF-paneel",
  // v6 F15 Buitenschrijnwerk
  exterior_window:        "Raam",
  exterior_sliding_window: "Schuifraam",
  exterior_door:          "Buitendeur",
  exterior_garage_door:   "Garagepoort",
  exterior_gate:          "Poort / hek",
  exterior_shutter:       "Luik",
  exterior_shutter_box:   "Rolluikkast",
  exterior_shutter_slats: "Rolluiklamellen",
  exterior_boeiboord:     "Boeiboord",
  exterior_dakoversteek:  "Dakoversteek",
  // v6 F32c
  exterior_cornice:       "Kroonlijst / kornis",
};

// v6 F42k P2 — Kinds die standaard het VEREENVOUDIGD schrijnwerkmodel
// gebruiken. Enkel ramen, deuren en poorten hebben een duidelijk hout/
// glas-onderscheid. Kasten, meubels en vrije objecten blijven op de
// klassieke parts-editor (technisch juister voor hun geometrie).
const SIMPLE_MODE_KINDS = new Set([
  "window", "door", "garage_door", "gate",                         // legacy
  "interior_door", "interior_sliding_door", "interior_window",     // binnen
  "exterior_window", "exterior_sliding_window", "exterior_door",
  "exterior_garage_door", "exterior_gate",                          // buiten
]);

// v6 F15 — Kind-lijst per zone_type. Legacy joinery-zones behouden hun
// oorspronkelijke set. Nieuwe zones krijgen de uitgebreide lijst.
const KINDS_BY_ZONE_TYPE = {
  joinery: [  // legacy
    "window", "door", "garage_door", "gate",
    "shutter", "shutter_box", "shutter_slats", "custom_object",
  ],
  interior_joinery: [
    "interior_door", "cabinet_door", "interior_sliding_door", "interior_window",
    "kitchen_front", "kitchen_cabinet", "bath_front", "bath_cabinet",
    "radiator_cabinet", "open_cabinet", "closed_cabinet", "desk_cabinet",
    "kitchen_tablet", "mdf_panel", "custom_object",
  ],
  exterior_joinery: [
    "exterior_window", "exterior_sliding_window", "exterior_door", "exterior_garage_door", "exterior_gate",
    "exterior_shutter", "exterior_shutter_box", "exterior_shutter_slats",
    "exterior_boeiboord", "exterior_dakoversteek", "exterior_cornice", "custom_object",
  ],
};

// v6 F16 §3 — Object-groepen per zone_type voor de picker-modal.
// Rustige, gescande layout: DEUREN & RAMEN eerst, dan MEUBELS & KASTEN,
// dan PANELEN & OVERIG. Kinds die niet expliciet in een groep zitten
// vallen terug op "Overig".
const OBJECT_GROUPS_BY_ZONE_TYPE = {
  joinery: [
    { label: "Deuren & ramen",  kinds: ["window", "door", "garage_door", "gate"] },
    { label: "Luiken & rolluiken", kinds: ["shutter", "shutter_box", "shutter_slats"] },
    { label: "Overig",          kinds: ["custom_object"] },
  ],
  interior_joinery: [
    { label: "Deuren & ramen",  kinds: ["interior_door", "cabinet_door", "interior_sliding_door", "interior_window"] },
    { label: "Meubels & kasten", kinds: [
      "kitchen_front", "kitchen_cabinet", "bath_front", "bath_cabinet",
      "radiator_cabinet", "open_cabinet", "closed_cabinet", "desk_cabinet",
    ] },
    { label: "Panelen & overig", kinds: ["kitchen_tablet", "mdf_panel", "custom_object"] },
  ],
  exterior_joinery: [
    { label: "Deuren & ramen",  kinds: ["exterior_window", "exterior_sliding_window", "exterior_door", "exterior_garage_door", "exterior_gate"] },
    { label: "Luiken & rolluiken", kinds: ["exterior_shutter", "exterior_shutter_box", "exterior_shutter_slats"] },
    { label: "Panelen & overig", kinds: ["exterior_boeiboord", "exterior_dakoversteek", "exterior_cornice", "custom_object"] },
  ],
};

const SIDE_OPTIONS = [
  { value: "interior", label: "Binnen",          short: "Binnen"          },
  { value: "exterior", label: "Buiten",          short: "Buiten"          },
  { value: "both",     label: "Binnen + Buiten", short: "Binnen + buiten" },
];

const sideMult = (side) => (side === "both" ? 2 : 1);

const newId = () => `j_${Math.random().toString(36).slice(2, 11)}`;

const fmtEUR = (n) => {
  try {
    return new Intl.NumberFormat("nl-BE", {
      style: "currency", currency: "EUR",
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(+n || 0).replace(/\u00A0/g, " ");
  } catch { return `€ ${(+n || 0).toFixed(2).replace(".", ",")}`; }
};

// v6 F15.1 → F15.3 — Kinds die naast L × H ook een `depth` (D) input nodig
// hebben. `custom_object` is er in F15.3 §4 bijgekomen (vrij object met
// tot 6 configureerbare vlakken).
const KINDS_WITH_DEPTH = new Set([
  "kitchen_cabinet", "bath_cabinet", "radiator_cabinet",
  "open_cabinet", "closed_cabinet", "desk_cabinet",
  "custom_object",
]);

// v6 F15.3 §3 — Semantische labels voor de maatvelden per kind. Standaard
// is Breedte + Hoogte. Sommige objecten (Dakoversteek, Keukentablet) worden
// als vloervormig gemeten en gebruiken Lengte + Breedte.
const DIM_LABELS_BY_KIND = {
  exterior_dakoversteek: { length: "Lengte (m)", height: "Breedte (m)", depth: "Diepte (m)" },
  kitchen_tablet:        { length: "Lengte (m)", height: "Breedte (m)", depth: "Diepte (m)" },
  // v6 F32c — Kroonlijst: horizontaal element, lengte = loopmeter langs gevel,
  // hoogte = ontwikkelde/geprofileerde hoogte van de sierlijst.
  exterior_cornice:      { length: "Lengte (m)", height: "Ontwikkelde hoogte (m)", depth: "Diepte (m)" },
};
const DIM_LABELS_DEFAULT = { length: "Breedte (m)", height: "Hoogte (m)", depth: "Diepte (m)" };

const dimLabelsFor = (kind) => DIM_LABELS_BY_KIND[kind] || DIM_LABELS_DEFAULT;

// v6 F15.1 — Formule-mapping per kind × key. Mirror van backend PART_DEFAULTS.
// Wordt gebruikt voor de live qty-preview per part in de UI + voor de zone-
// totalen (m² area / lm length) die de werk-rijen aansturen.
// Elke fn krijgt het object en geeft één-instantie qty terug (vóór count &
// sides multipliers).
const _lh = (o) => (+o.length || 0) * (+o.height || 0);
const _ld = (o) => (+o.length || 0) * (+o.depth || 0);
const _hd = (o) => (+o.height || 0) * (+o.depth || 0);
const _perim = (o) => 2 * (+o.length || 0) + 2 * (+o.height || 0);
const _l = (o) => (+o.length || 0);
const _h = (o) => (+o.height || 0);
const _l02 = (o) => (+o.length || 0) * 0.20;
const _l03 = (o) => (+o.length || 0) * 0.30;

// v6 F32a — Nieuwe kaderformules voor buitendeur/garagepoort/poort:
// 2H + B (linker + rechter stijl + bovendorpel; onderzijde = dorpel/steen,
// geen te schilderen kader). Raam blijft 2H + 2B (rondom kader).
const _door_frame = (o) => 2 * (+o.height || 0) + (+o.length || 0);

// v6 F16 S4 — Formule-mapping GEEXPORTEERD zodat lib/autoSource.js
// per-part totals kan berekenen voor de UI-preview (met dezelfde formules
// als backend PART_DEFAULTS).
export const PART_FORMULA = {
  interior_door:          { door_blade: _lh, frame: _perim },
  cabinet_door:           { door_blade: _lh },
  interior_sliding_door:  { door_blade: _lh, frame: _perim },
  interior_window:        { window_pane: _lh, frame: _perim, sill: _l },
  kitchen_front:          { front: _lh },
  kitchen_cabinet:        { side_panels: _hd, front: _lh, top: _ld, back: _lh, shelves: _ld },
  bath_front:             { front: _lh },
  bath_cabinet:           { side_panels: _hd, front: _lh, top: _ld, back: _lh, shelves: _ld },
  radiator_cabinet:       { side_panels: _hd, front: _lh, top: _ld, back: _lh, grille: _l },
  open_cabinet:           { side_panels: _hd, top: _ld, bottom: _ld, back: _lh, shelves: _ld },
  closed_cabinet:         { side_panels: _hd, front: _lh, top: _ld, bottom: _ld, back: _lh, shelves: _ld },
  desk_cabinet:           { side_panels: _hd, front: _lh, top: _ld, back: _lh, shelves: _ld },
  kitchen_tablet:         { top: _lh, front_edge: _l, side_edges: _h },
  mdf_panel:              { panel: _lh },
  // v6 F32b — Luiken (shutters) en Rolluikkast (shutter_box) verwijderd uit
  // exterior_window: die worden nu enkel via aparte objecttypes ingegeven.
  exterior_window:        { window_pane: _lh, inside: _lh, frame: _perim },
  // v6 F32a — Kader = 2H + B voor buitendeur/garagepoort/poort.
  exterior_door:          { door_blade: _lh, inside: _lh, frame: _door_frame, toplight: _l03 },
  exterior_garage_door:   { front: _lh, back: _lh, frame: _door_frame },
  exterior_gate:          { surface: _lh, structure: _door_frame },
  exterior_shutter:       { surface: _lh },
  exterior_shutter_box:   { box: _lh },
  exterior_shutter_slats: { slats: _lh, back_side: _lh },
  exterior_boeiboord:     { surface: _lh, as_lm: _l },
  exterior_dakoversteek:  { surface: _lh },
  // v6 F32c — Kroonlijst / kornis: enkel vlak (L × H).
  exterior_cornice:       { surface: _lh },
  custom_object:          { front: _lh, back: _lh, side_panels: _hd, top: _ld, bottom: _ld },
};

// v6 F15.3 §5 — Sides-opties per kind × part-key. Wanneer aanwezig toont
// PartRow een compact togglegroepje met deze opties. Als niet gedefinieerd
// blijft sides op de default staan (uit backend PART_DEFAULTS).
const PART_SIDES_OPTIONS = {
  // Front van kasten/meubels — 1 of 2 zijden (F15.2)
  "kitchen_front::front":   [1, 2],
  "kitchen_cabinet::front": [1, 2],
  "bath_front::front":      [1, 2],
  "bath_cabinet::front":    [1, 2],
  "closed_cabinet::front":  [1, 2],
  // Luiken — 1, 2 (default), of 4 zijden (dubbel luik met vleugels)
  "exterior_shutter::surface": [1, 2, 4],
};

// Merge user-parts overrides met defaults, retourneert een genormaliseerde
// lijst {key, label, unit, sides, enabled, count} klaar voor UI + qty calc.
// v6 F16 S4 — Ook mergePartStates geëxporteerd voor autoSource-resolver.
export function mergePartStates(obj, kindDefaults) {
  const defs = kindDefaults || [];
  const userByKey = new Map((obj.parts || []).map((p) => [p.key, p]));
  return defs.map((d) => {
    const u = userByKey.get(d.key);
    return {
      key: d.key,
      label: d.label,
      unit: d.unit,
      sides: (u && u.sides != null) ? u.sides : (d.sides || 1),
      enabled: u ? !!u.enabled : !!d.enabled,
      count: (u && u.count != null) ? +u.count : +d.count,
      defaultCount: +d.count,
    };
  });
}

// Bereken area_m2 + length_lm voor één object (mirror van backend
// compute_joinery_object_quantities). Legacy: als kind geen defaults heeft
// vallen we terug op oude L×H×side_multiplier berekening.
function computeObjectQty(obj, kindDefaults) {
  const kind = obj?.kind || "custom_object";
  const objCount = Math.max(1, +obj?.count || 1);
  if ((obj?.measure_mode || "per_piece") === "forfait") {
    return { area_m2: 0, length_lm: 0, pieces: objCount };
  }
  // v6 F42k v14f-fix — Explicite measurement_mode heeft voorrang op simple_mode logica.
  // v14at-P10-A — Nieuwe modes:
  //   "area_m2+frame_lm" → serieel beide compute-paden en concat
  //   "none"             → object bewust gedeactiveerd (zero buckets)
  const _mm = obj?.measurement_mode;
  if (_mm === "none") {
    return { area_m2: 0, length_lm: 0, pieces: objCount };
  }
  if (_mm === "area_m2+frame_lm") {
    const areaR = computeObjectQty({ ...obj, measurement_mode: "area_m2" }, kindDefaults);
    const frameR = computeObjectQty({ ...obj, measurement_mode: "frame_lm" }, kindDefaults);
    return {
      area_m2: areaR.area_m2 || 0,
      length_lm: frameR.length_lm || 0,
      pieces: Math.max(areaR.pieces || 0, frameR.pieces || 0),
    };
  }
  if (_mm === "area_m2" || _mm === "frame_lm") {
    const L = +obj.length || 0;
    const H = +obj.height || 0;
    const sidesKey = obj.window_sides || "outside";
    const sidesMult = sidesKey === "both" ? 2 : 1;
    const isWin = kind.includes("window");
    const isDoor = kind.includes("door") || kind.includes("gate");
    if (_mm === "area_m2") {
      let ratio = +obj.simple_wood_ratio;
      if (!Number.isFinite(ratio)) ratio = 1.0;
      ratio = Math.min(1, Math.max(0, ratio));
      const effRatio = isDoor ? ratio : 1.0;
      let area = L * H * objCount * sidesMult * effRatio;
      const sl = obj?.sidelights;
      if (isDoor && sl?.enabled) {
        const slQty = (+sl.count || 0) * (+sl.width || 0) * (+sl.height || 0) * objCount * sidesMult;
        if (slQty > 0) area += slQty;
      }
      return { area_m2: area, length_lm: 0, pieces: objCount };
    }
    // frame_lm
    if (isWin) {
      const fixed = Math.max(0, +obj.simple_fixed_parts || 0);
      const moving = Math.max(0, +obj.simple_moving_parts || 0);
      const forceSliding = kind === "exterior_sliding_window";
      let nFixed = fixed, nMoving = moving;
      let movingWidths = Array.isArray(obj.window_moving_widths)
        ? obj.window_moving_widths.map((w) => +w || 0).filter((w) => w > 0)
        : [];
      if (obj.window_sliding || forceSliding) {
        let split = +obj.window_sliding_split_ratio;
        if (!Number.isFinite(split)) split = 0.5;
        split = Math.min(1, Math.max(0, split));
        nFixed = 1; nMoving = 1;
        movingWidths = [L * (1 - split)];
      } else {
        const nt = Math.max(1, nFixed + nMoving);
        const dw = L / nt;
        while (movingWidths.length < nMoving) movingWidths.push(dw);
        movingWidths = movingWidths.slice(0, nMoving);
      }
      const nTotal = nFixed + nMoving;
      let nInterne = Math.max(0, nTotal - 1);
      const effNoMiddle = !!obj.window_double_movers_no_middle || (obj.window_middle_pillar === false);
      if (effNoMiddle && !(obj.window_sliding || forceSliding)) nInterne = Math.max(0, nInterne - 1);
      const nVerticalen = 2 + nInterne;
      const hOuter = 2 * L;
      const vFixed = nVerticalen * H;
      const hMoving = movingWidths.reduce((s, w) => s + 2 * w, 0);
      const vMoving = nMoving * 2 * H;
      const toplightLm = obj.window_has_toplight ? L : 0;
      const lmPer = hOuter + vFixed + hMoving + vMoving + toplightLm;
      return { area_m2: 0, length_lm: lmPer * objCount * sidesMult, pieces: objCount };
    }
    if (isDoor) {
      const kaderPer = 2 * H + L + (obj.door_double ? H : 0);
      return { area_m2: 0, length_lm: kaderPer * objCount * sidesMult, pieces: objCount };
    }
    return { area_m2: 0, length_lm: 2 * (L + H) * objCount * sidesMult, pieces: objCount };
  }
  // v6 F42k P2 v12 — Simple mode gesplitst per kind-familie (raam / deur / overig).
  if (obj?.simple_mode) {
    const L = +obj.length || 0;
    const H = +obj.height || 0;
    const fixed = Math.max(0, +obj.simple_fixed_parts || 0);
    const moving = Math.max(0, +obj.simple_moving_parts || 0);
    let ratio = +obj.simple_wood_ratio;
    if (!Number.isFinite(ratio)) ratio = 0.30;
    ratio = Math.min(1, Math.max(0, ratio));
    const isWin = kind.includes("window");
    const isDoor = kind.includes("door") || kind.includes("gate");
    // v13 — Nieuw kind exterior_sliding_window → dwing schuifraam-modus
    const forceSliding = kind === "exterior_sliding_window";
    // v13 — Zijden-multiplier op ALLE ramen én deuren (niet enkel schuiframen).
    const sidesKey = obj.window_sides || "outside";
    const sidesMult = sidesKey === "both" ? 2 : 1;

    if (isWin) {
      if (obj.window_has_cross_division) {
        return { area_m2: L * H * objCount * sidesMult, length_lm: 0, pieces: objCount };
      }
      let nFixed = fixed, nMoving = moving;
      let movingWidths = Array.isArray(obj.window_moving_widths) ? obj.window_moving_widths.map((w) => +w || 0).filter((w) => w > 0) : [];
      if (obj.window_sliding || forceSliding) {
        let split = +obj.window_sliding_split_ratio;
        if (!Number.isFinite(split)) split = 0.5;
        split = Math.min(1, Math.max(0, split));
        nFixed = 1; nMoving = 1;
        movingWidths = [L * (1 - split)];
      } else {
        const nTotalW = Math.max(1, nFixed + nMoving);
        const defaultW = L / nTotalW;
        while (movingWidths.length < nMoving) movingWidths.push(defaultW);
        movingWidths = movingWidths.slice(0, nMoving);
      }
      const nTotal = nFixed + nMoving;
      let nInterne = Math.max(0, nTotal - 1);
      // v13 — Combineer legacy `window_double_movers_no_middle` met nieuwe `window_middle_pillar`
      const effectiveNoMiddle = !!obj.window_double_movers_no_middle || (obj.window_middle_pillar === false);
      if (effectiveNoMiddle && !(obj.window_sliding || forceSliding)) nInterne = Math.max(0, nInterne - 1);
      const nVerticalen = 2 + nInterne;
      const hOuter = 2 * L;
      const vFixed = nVerticalen * H;
      const hMoving = movingWidths.reduce((s, w) => s + 2 * w, 0);
      const vMoving = nMoving * 2 * H;
      // v13 — Vast bovenlicht: extra horizontale scheidingslijn (+ L lm)
      const toplightLm = obj.window_has_toplight ? L : 0;
      const lmPer = hOuter + vFixed + hMoving + vMoving + toplightLm;
      return { area_m2: 0, length_lm: lmPer * objCount * sidesMult, pieces: objCount };
    }

    if (isDoor) {
      const doorArea = L * H * ratio * objCount * sidesMult;
      let kaderPer = 2 * H + L;
      if (obj.door_double) kaderPer += H;
      const kaderLm = kaderPer * objCount * sidesMult;
      let area = doorArea;
      const sl = obj?.sidelights;
      if (sl?.enabled) {
        const slQty = (+sl.count || 0) * (+sl.width || 0) * (+sl.height || 0) * objCount * sidesMult;
        if (slQty > 0) area += slQty;
      }
      return { area_m2: area, length_lm: kaderLm, pieces: objCount };
    }

    // Overig: legacy simple
    const totalParts = Math.max(1, fixed + moving);
    const totalFace = L * H * totalParts * objCount;
    return {
      area_m2: totalFace * ratio,
      length_lm: 2 * (L + H) * objCount,
      pieces: objCount,
    };
  }
  const defs = kindDefaults || [];
  const formulas = PART_FORMULA[kind] || {};
  if (defs.length === 0) {
    // Legacy fallback: oude L×H×side_multiplier (voor generieke joinery zones)
    const sideMultLegacy = (obj?.side === "both") ? 2 : 1;
    return {
      area_m2: _lh(obj) * sideMultLegacy * objCount,
      length_lm: 0,
      pieces: objCount,
    };
  }
  const states = mergePartStates(obj, defs);
  let area = 0, length = 0;
  for (const p of states) {
    if (!p.enabled || p.count <= 0) continue;
    const fn = formulas[p.key];
    if (!fn) continue;
    const q = fn(obj) * p.count * (p.sides || 1) * objCount;
    if (q <= 0) continue;
    if (p.unit === "m2") area += q;
    else if (p.unit === "lm") length += q;
  }
  // v6 F15.3 §6 — Zijlichten voor buitendeur (los raamdeel).
  const sl = obj?.sidelights;
  if (sl?.enabled) {
    const slQty = (+sl.count || 0) * (+sl.width || 0) * (+sl.height || 0) * objCount;
    if (slQty > 0) area += slQty;
  }
  return { area_m2: area, length_lm: length, pieces: objCount };
}

export function JoineryBlock({ zone, onUpdateZone, priceCodes = [], templates = [], partsDefaults = {}, autoSources: autoSourcesProp, calc }) {
  // v6 F20.1 — Fallback naar AutoSourcesContext zodat de resolver ook werkt
  // wanneer ExteriorZoneFlow (of andere flows) de prop niet expliciet doorgeeft.
  const autoSourcesCtx = useContext(AutoSourcesContext);
  const autoSources = autoSourcesProp && Object.keys(autoSourcesProp).length > 0
    ? autoSourcesProp
    : (autoSourcesCtx || {});
  const objects = zone.joinery_objects || [];
  const works = zone.joinery_works || [];
  // v6 F15 — Detect legacy vs v15 zone. Voor v15-zones is de kant impliciet
  // en tonen we de "Binnen/Buiten/Both" toggle NIET meer; kinds zijn ook
  // gefilterd op zone_type via KINDS_BY_ZONE_TYPE.
  const isV15Interior = zone.zone_type === "interior_joinery";
  const isV15Exterior = zone.zone_type === "exterior_joinery";
  const isV15 = isV15Interior || isV15Exterior;
  const implicitSide = isV15Interior ? "interior" : isV15Exterior ? "exterior" : null;
  const defaultSide = implicitSide || zone.joinery_default_side || "exterior";
  const activeTemplateId = zone.joinery_template_id || "";

  // v6 F15 — Sync joinery_default_side voor v15-zones (impliciet).
  useEffect(() => {
    if (isV15 && zone.joinery_default_side !== implicitSide) {
      onUpdateZone({ joinery_default_side: implicitSide });
    }
  }, [isV15, implicitSide, zone.joinery_default_side, onUpdateZone]);

  // v6 F15.3 §1 — Object-open state: per objectId → bool. Bestaande objecten
  // starten DICHT (rustiger overzicht); nieuwe objecten openen automatisch
  // (via addObject) én de "Alles open/dicht" bar laat de gebruiker snel
  // schakelen.
  const [openMap, setOpenMap] = useState({});
  const allOpen = objects.length > 0 && objects.every((o) => !!openMap[o.id]);
  const toggleAll = () => {
    const next = {};
    objects.forEach((o) => { next[o.id] = !allOpen; });
    setOpenMap(next);
  };
  const toggleOne = (oid) => setOpenMap((m) => ({ ...m, [oid]: !m[oid] }));

  // v3 — L × H is de enige meetmethode. Auto-migreer legacy zones met
  // joinery_measure_mode != "by_dimension" naar by_dimension zodat de backend
  // ook L × H gebruikt.
  useEffect(() => {
    if (zone.joinery_measure_mode !== "by_dimension") {
      onUpdateZone({ joinery_measure_mode: "by_dimension" });
    }
  }, [zone.joinery_measure_mode, onUpdateZone]);

  // Lookup: price_code by id
  const pcById = useMemo(() => {
    const m = {};
    priceCodes.forEach((p) => { m[p.id] = p; });
    return m;
  }, [priceCodes]);

  // v6 F15 — Beschikbare schrijnwerk-sjablonen gefilterd op template_context.
  // Voor v15-zones tonen we ENKEL de 4 kern-templates (basis + deco per kant),
  // niet meer de legacy YD-sjablonen (die zijn nu active=false). Legacy zones
  // (zone_type="joinery") gedragen zich zoals voorheen.
  const joineryTemplates = useMemo(() => {
    const active = (templates || []).filter((t) => t.active !== false);
    const isInteriorJoinery = (t) => t.template_context === "interior_joinery";
    const isInteriorDeco    = (t) => t.template_context === "interior_joinery_deco";
    const isExteriorJoinery = (t) => t.template_context === "exterior_joinery";
    const isExteriorDeco    = (t) => t.template_context === "exterior_joinery_deco";
    const isLegacyJoinery = (t) => {
      if (t.template_context) return false;
      const cats = (t.categories && t.categories.length) ? t.categories : [t.category];
      return cats.includes("joinery");
    };
    let filtered;
    if (isV15Interior) {
      filtered = active.filter((t) => isInteriorJoinery(t) || isInteriorDeco(t));
    } else if (isV15Exterior) {
      filtered = active.filter((t) => isExteriorJoinery(t) || isExteriorDeco(t));
    } else if (defaultSide === "interior") {
      filtered = active.filter((t) => isInteriorJoinery(t) || isInteriorDeco(t) || isLegacyJoinery(t));
    } else if (defaultSide === "exterior") {
      filtered = active.filter((t) => isExteriorJoinery(t) || isExteriorDeco(t) || isLegacyJoinery(t));
    } else {
      filtered = active.filter(
        (t) => isInteriorJoinery(t) || isInteriorDeco(t) || isExteriorJoinery(t) || isExteriorDeco(t) || isLegacyJoinery(t)
      );
    }
    return filtered;
  }, [templates, defaultSide, isV15Interior, isV15Exterior]);

  const activeTemplate = useMemo(
    () => joineryTemplates.find((t) => t.id === activeTemplateId) || null,
    [joineryTemplates, activeTemplateId]
  );
  // v6 F11.7 — Template-mapping bug fix: detecteer ontbrekende template-ID.
  // Wanneer `joinery_template_id` gezet is maar er geen overeenkomst in DB
  // bestaat, tonen we "Template niet gevonden" i.p.v. stil terug te vallen.
  // v6 F12 — Onderscheid tussen "ontbrekend in DB" (echt fout) en
  // "bestaat wél maar filtert weg door side" (waarschuwing).
  // v6 F16.2 — Onderscheid tussen "template inactief/verouderd" (rustige amber
  // waarschuwing) en "echt weg uit DB" (rode error). We laden nu ALLE templates
  // (incl. active=false); een gedeactiveerde template die vroeger de zone
  // voedde wordt hier gedetecteerd zodat de user rustig kan migreren.
  const legacyTemplate = useMemo(
    () => (templates || []).find((t) => t.id === activeTemplateId && t.active === false) || null,
    [templates, activeTemplateId]
  );
  const templateExistsInDb = !!activeTemplateId && !!(templates || []).find((t) => t.id === activeTemplateId);
  const templateMissing = !!activeTemplateId && templates.length > 0 && !templateExistsInDb;
  // wrongSide alleen bij actieve templates — legacy templates krijgen hun eigen tak
  const templateWrongSide = !!activeTemplateId && templateExistsInDb && !legacyTemplate && !activeTemplate;
  const templateLegacy = !!legacyTemplate;

  // v6 F15.1 — Zone-totalen komen nu uit de parts-berekening (mirror van
  // backend `compute_joinery_object_quantities`). Voor legacy zones zonder
  // parts-defaults valt `computeObjectQty` terug op oude L×H×side_multiplier.
  // Dit houdt de UI-preview 100% in sync met de backend calc.
  const objectQtys = useMemo(
    () => objects.map((obj) => computeObjectQty(obj, partsDefaults[obj.kind] || [])),
    [objects, partsDefaults]
  );
  const totalM2 = useMemo(
    () => objectQtys.reduce((acc, q) => acc + (q.area_m2 || 0), 0),
    [objectQtys]
  );
  const totalLm = useMemo(
    () => objectQtys.reduce((acc, q) => acc + (q.length_lm || 0), 0),
    [objectQtys]
  );

  // Driver voor elke werk-card
  const zoneQty = totalM2;
  const zoneQtyUnit = "m2";

  const nextNameForKind = (kind) => {
    const base = OBJECT_KIND_LABEL[kind] || "Object";
    const existing = objects.filter((o) => o.kind === kind).length;
    return `${base} ${existing + 1}`;
  };

  const setDefaultSide = (side) => onUpdateZone({ joinery_default_side: side });

  // ── Sjabloon kiezen ──
  // Vervang `joinery_works` door de default-checked lines van het nieuwe
  // sjabloon. Behoud bestaande overrides (unit_price) wanneer dezelfde
  // price_code_id in beide sjablonen voorkomt.
  const setTemplate = (tplId) => {
    const tpl = joineryTemplates.find((t) => t.id === tplId);
    if (!tpl) {
      onUpdateZone({ joinery_template_id: tplId, joinery_works: [], user_dismissed_line_ids: [] });
      return;
    }
    // v6 F22 — Preserveer bestaande unit_price-overrides door match op
    // template_line_id (uniek per regel, ook bij dubbele price_code_id voor
    // enkelzijde vs dubbelzijde varianten). Extra werken buiten sjabloon
    // gaan verloren bij template-switch (was ook zo in F16-gedrag).
    const existingByLineId = new Map(
      (works || []).filter((w) => w.template_line_id).map((w) => [w.template_line_id, w])
    );
    const newWorks = (tpl.template_lines || [])
      .filter((ln) => ln.default_checked !== false)
      .filter((ln) => {
        // v6 F25 — Alleen relevante regels toevoegen (qty > 0 of forfait).
        // De useEffect zorgt voor auto-add zodra objecten worden toegevoegd.
        if (!ln.auto_source) return true;  // forfait / manueel
        const q = resolveAutoSource(ln.auto_source, zone, autoSources || {}, partsDefaults || {});
        const mult = +ln.auto_source_multiplier || 1;
        return (q * mult) > 0;
      })
      .map((ln) => {
        const pc = pcById[ln.price_code_id];
        const ex = existingByLineId.get(ln.id);
        const description = ln.description_override || pc?.description || "";
        const unit_price = ex?.unit_price != null && ex.unit_price !== 0
          ? ex.unit_price
          : (ln.unit_price_override != null ? +ln.unit_price_override : (pc ? +pc.default_price : 0));
        return {
          id: ex?.id || newId(),
          work_kind: "custom",
          description,
          price_code_id: ln.price_code_id,
          template_line_id: ln.id || null,
          auto_source: ln.auto_source || null,
          auto_source_multiplier: (ln.auto_source_multiplier != null ? +ln.auto_source_multiplier : null),
          unit_price,
          object_ids: null,
        };
      });
    onUpdateZone({ joinery_template_id: tplId, joinery_works: newWorks, user_dismissed_line_ids: [] });
  };

  // Toggle één template-line aan/uit in joinery_works.
  // v6 F22 — Match op template_line_id voor sjabloon-regels (uniek per regel,
  // ook bij dubbele price_code). Voor extra werken buiten sjabloon (fakeLine
  // met _activeWorkId marker) matchen op work.id. Anders fallback op pc.
  // v6 F23 — Choice-group radio-gedrag: als de regel een `choice_group` heeft,
  // zet alle andere actieve regels in dezelfde groep uit vóór we deze aanzetten.
  const toggleLine = (line) => {
    const pc = pcById[line.price_code_id];
    let exIdx;
    if (line._activeWorkId) {
      exIdx = works.findIndex((w) => w.id === line._activeWorkId);
    } else if (line.id) {
      exIdx = works.findIndex((w) => w.template_line_id === line.id);
    } else {
      exIdx = works.findIndex((w) => !w.template_line_id && w.price_code_id === line.price_code_id);
    }
    // v6 F25 — Onthoud bewust uitgeschakelde sjabloon-regels zodat de
    // auto-add-useEffect ze niet opnieuw activeert.
    const currentDismissed = zone.user_dismissed_line_ids || [];
    if (exIdx >= 0) {
      // Uit: template-linked werken → voeg toe aan dismissed set.
      const removedWork = works[exIdx];
      const isTemplateLinked = !!removedWork.template_line_id;
      const nextDismissed = (isTemplateLinked && !currentDismissed.includes(removedWork.template_line_id))
        ? [...currentDismissed, removedWork.template_line_id]
        : currentDismissed;
      onUpdateZone({
        joinery_works: works.filter((_, i) => i !== exIdx),
        user_dismissed_line_ids: nextDismissed,
      });
      return;
    }
    // Aan: eerst eventuele andere actieve regels in dezelfde choice-group
    // eruit filteren (radio-gedrag, laatste keuze wint).
    let base = works;
    const group = line.choice_group;
    if (group && activeTemplate?.template_lines?.length) {
      const groupLineIds = new Set(
        activeTemplate.template_lines
          .filter((L) => L.choice_group === group && L.id)
          .map((L) => L.id)
      );
      base = works.filter((w) => !w.template_line_id || !groupLineIds.has(w.template_line_id));
    }
    const description = line.description_override || pc?.description || "";
    const unit_price = line.unit_price_override != null
      ? +line.unit_price_override
      : (pc ? +pc.default_price : 0);
    // v6 F25 — Verwijder deze line.id uit dismissed set (user activeert bewust).
    const nextDismissed = line.id
      ? currentDismissed.filter((id) => id !== line.id)
      : currentDismissed;
    onUpdateZone({
      joinery_works: [
        ...base,
        {
          id: newId(),
          work_kind: "custom",
          description,
          price_code_id: line.price_code_id,
          template_line_id: line.id || null,
          auto_source: line.auto_source || null,
          auto_source_multiplier: (line.auto_source_multiplier != null ? +line.auto_source_multiplier : null),
          unit_price,
          object_ids: null,
        },
      ],
      user_dismissed_line_ids: nextDismissed,
    });
  };

  const updateWork = (wid, patch) =>
    onUpdateZone({
      joinery_works: works.map((w) => (w.id === wid ? { ...w, ...patch } : w)),
    });

  // ── Objects CRUD ──
  const addObject = (kind) => {
    const useSimple = SIMPLE_MODE_KINDS.has(kind);
    // v13 — Zone-context bepaalt standaard-zijde. Buitenschrijnwerk → outside;
    // Binnenschrijnwerk → inside; overige zones → outside.
    const zoneSideDefault = zone.zone_type === "interior_joinery" ? "inside" : "outside";
    // v13 — Nieuw objecttype exterior_sliding_window → schuifraam automatisch.
    const isSliding = kind === "exterior_sliding_window";
    const isWin = kind.includes("window");
    const newObj = {
      id: newId(),
      kind,
      name: nextNameForKind(kind),
      count: 1,
      measure_mode: "by_dimension",
      length: 0,
      height: 0,
      forfait_amount: 0,
      side: defaultSide,
      parent_object_id: null,
      work_lines: [],
      notes: "",
      // v6 F42k P2 — Vereenvoudigd model voor ramen/deuren/poorten.
      simple_mode: useSimple,
      // v14f-fix — Nieuwe segmented control default: Oppervlak m² (pure m² output).
      measurement_mode: useSimple ? "area_m2" : null,
      // v13 — Nieuwe raam-defaults: 0 vast, 2 bewegend (klassiek dubbel opendraaiend)
      simple_fixed_parts: isWin && !isSliding ? 0 : 0,
      simple_moving_parts: isWin && !isSliding ? 2 : 1,
      simple_wood_ratio: (kind.includes("door") || kind.includes("gate")) ? 1.0 : 0.30,
      // v6 F42k P2 v12 — Raamconstructie
      window_moving_widths: [],
      window_double_movers_no_middle: true, // v14f — spiegel van window_middle_pillar=false
      window_middle_pillar: false,         // v14f — nieuwe default: geen middenstijl
      window_has_toplight: false,          // v13 — vast bovenlicht
      window_toplight_ratio: 0.25,         // v13 — 1/4 hoogte default
      window_has_cross_division: false,
      window_sliding: isSliding,            // v13 — dwing schuifraam-flag bij nieuw sliding-kind
      window_sliding_split_ratio: 0.5,
      window_sides: zoneSideDefault,        // v13 — outside (buitenzone) of inside (binnenzone)
      // v6 F42k P2 v12 — Deurspecifiek
      door_double: false,
    };
    onUpdateZone({ joinery_objects: [...objects, newObj] });
    // v6 F15.3 §1 — Nieuwe objecten openen automatisch.
    setOpenMap((m) => ({ ...m, [newObj.id]: true }));
  };

  const updateObject = (oid, patch) =>
    onUpdateZone({
      joinery_objects: objects.map((o) => (o.id === oid ? { ...o, ...patch } : o)),
    });

  const removeObject = (oid) =>
    onUpdateZone({
      joinery_objects: objects
        .filter((o) => o.id !== oid)
        .map((o) => (o.parent_object_id === oid ? { ...o, parent_object_id: null } : o)),
    });

  // Map active price_code_id → work (voor extra werken zonder template_line_id)
  const activeByPc = useMemo(() => {
    const m = {};
    works.forEach((w) => { if (w.price_code_id && !w.template_line_id) m[w.price_code_id] = w; });
    return m;
  }, [works]);

  // v6 F22 — Map template_line_id → work zodat sjabloon-regels met dezelfde
  // price_code (bv. enkelzijde vs dubbelzijde) apart aan/uit kunnen.
  const activeByLine = useMemo(() => {
    const m = {};
    works.forEach((w) => { if (w.template_line_id) m[w.template_line_id] = w; });
    return m;
  }, [works]);

  // Zone subtotaal (uit calc)
  const zoneSubtotal = useMemo(
    () => (calc?.lines || [])
      .filter((l) => l.zone_id === zone.id && l.kind === "joinery")
      .reduce((s, l) => s + (+l.total_excl || 0), 0),
    [calc, zone.id]
  );

  // v6 F25 — Semi-auto activation & cleanup.
  // Reageert op wijzigingen in de objecten (parts aan/uit, count, dimensies)
  // en op template-wissel. Doet in één pass:
  //   (a) auto-add: default_checked template-regels die relevant zijn geworden
  //       (qty > 0) worden aan joinery_works toegevoegd, tenzij de regel in
  //       user_dismissed_line_ids staat of al actief is of een concurrent uit
  //       dezelfde choice_group actief is.
  //   (b) cleanup: template-linked actieve werken waarvan qty = 0 worden
  //       verwijderd (bv. object of onderdeel weggehaald). Extra werken
  //       zonder template_line_id blijven staan.
  // Guard: gebruik ref om reconciliation-lus te voorkomen wanneer de
  // useEffect zelf onUpdateZone triggert.
  const reconcileGuardRef = useRef(false);
  useEffect(() => {
    if (!activeTemplate) return;
    if (reconcileGuardRef.current) {
      reconcileGuardRef.current = false;
      return;
    }
    const dismissed = new Set(zone.user_dismissed_line_ids || []);
    const tplLines = activeTemplate.template_lines || [];
    const tplLineById = new Map(tplLines.filter((l) => l.id).map((l) => [l.id, l]));

    // Bereken qty per template-line
    const qtyByLineId = new Map();
    for (const line of tplLines) {
      if (!line.id) continue;
      if (!line.auto_source) { qtyByLineId.set(line.id, 1); continue; }  // forfait
      const resolved = resolveAutoSource(line.auto_source, zone, autoSources || {}, partsDefaults || {});
      const mult = +line.auto_source_multiplier || 1;
      qtyByLineId.set(line.id, resolved * mult);
    }

    // Actieve choice_group → line.id met huidig actieve
    const activeInGroup = new Map();
    for (const w of works) {
      if (!w.template_line_id) continue;
      const line = tplLineById.get(w.template_line_id);
      if (line?.choice_group) activeInGroup.set(line.choice_group, w.template_line_id);
    }

    // (b) Cleanup: verwijder template-linked works met qty=0 EN orphans
    //     (template_line_id niet meer in current template — bv. na F20/F26
    //     migratie die line-ids regenereert). Extra werken zonder
    //     template_line_id blijven altijd staan.
    const worksAfterCleanup = works.filter((w) => {
      if (!w.template_line_id) return true;  // extra werk buiten sjabloon
      const line = tplLineById.get(w.template_line_id);
      if (!line) return false;  // v6 F26 — orphan → weg (one-shot cleanup)
      const q = qtyByLineId.get(w.template_line_id) ?? 0;
      return q > 0;
    });

    // (a) Auto-add: relevante regels die niet gedismist, niet actief zijn,
    // en (bij choice_group) niet met een concurrent actief. v6 F26 —
    // Regels ZONDER choice_group worden automatisch actief zodra qty > 0
    // (het onderdeel is expliciet aangevinkt in het object). Regels IN een
    // choice_group volgen default_checked.
    const additions = [];
    const alreadyActiveIds = new Set(worksAfterCleanup.filter((w) => w.template_line_id).map((w) => w.template_line_id));
    for (const line of tplLines) {
      if (!line.id) continue;
      if (dismissed.has(line.id)) continue;
      if (alreadyActiveIds.has(line.id)) continue;
      const q = qtyByLineId.get(line.id) ?? 0;
      if (q <= 0) continue;
      // v6 F26 + v14e final — Auto-add gedrag:
      //  * groep-regel → alleen als default_checked=True én groep nog vrij
      //  * losse regel (geen choice_group):
      //      - respecteer default_checked=false ⇒ NIET auto-add (v14e-fix)
      //        (voorheen werd bv. S-01.01 'Beschermen en afplakken' altijd
      //        auto-aangevinkt in Buitenschrijnwerk-sjablonen ondanks
      //        default_checked=false; user moest zelf uitvinken).
      //      - anders (default_checked=true of null-legacy) → auto-add
      //        bij qty > 0 (behoud F26-gedrag voor auto_source-regels).
      if (line.choice_group) {
        if (!line.default_checked) continue;
        if (activeInGroup.has(line.choice_group)) continue;
      } else {
        if (line.default_checked === false) continue;
      }
      const pc = pcById[line.price_code_id];
      const description = line.description_override || pc?.description || "";
      const unit_price = line.unit_price_override != null
        ? +line.unit_price_override
        : (pc ? +pc.default_price : 0);
      additions.push({
        id: newId(),
        work_kind: "custom",
        description,
        price_code_id: line.price_code_id,
        template_line_id: line.id,
        auto_source: line.auto_source || null,
        auto_source_multiplier: (line.auto_source_multiplier != null ? +line.auto_source_multiplier : null),
        unit_price,
        object_ids: null,
      });
      if (line.choice_group) activeInGroup.set(line.choice_group, line.id);
    }

    if (additions.length === 0 && worksAfterCleanup.length === works.length) return;
    reconcileGuardRef.current = true;
    onUpdateZone({ joinery_works: [...worksAfterCleanup, ...additions] });
  }, [zone.joinery_objects, activeTemplate, zone.user_dismissed_line_ids]);

  return (
    <div className="space-y-3" data-testid={`joinery-block-${zone.id}`}>
      {/* ───── 1. Uitvoering — enkel zichtbaar voor legacy joinery-zones.
              Voor v15 (binnen/buiten) is de kant impliciet uit zone_type. */}
      {!isV15 && (
        <div className="border border-[#E5E2DC] rounded-xl bg-[#FAF9F6] p-3">
          <div className="text-[11px] uppercase tracking-[0.1em] font-bold text-[#5D7259] mb-2">
            Uitvoering (standaard voor nieuwe objecten)
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid={`joinery-zone-side-${zone.id}`}>
            {SIDE_OPTIONS.map(({ value, label }) => (
              <button
                key={value}
                type="button"
                onClick={() => setDefaultSide(value)}
                className={`h-10 px-4 rounded-lg text-sm font-medium border transition ${
                  defaultSide === value
                    ? "bg-[#B07A4F] text-white border-[#B07A4F]"
                    : "bg-white border-[#E5E2DC] hover:border-[#B07A4F] text-[#4F4F4D]"
                }`}
                data-testid={`joinery-zone-side-${zone.id}-${value}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ───── 2. Object-picker — één knop, opent modal met groepen ───── */}
      <JoineryObjectPicker
        zoneId={zone.id}
        zoneType={zone.zone_type}
        onAdd={addObject}
      />

      {/* ───── 3. Flat object-rijen ───── */}
      {objects.length === 0 ? (
        <div className="text-sm text-[#4F4F4D] italic py-2">
          Geen objecten. Voeg er hierboven toe.
        </div>
      ) : (
        <div className="border border-[#E5E2DC] rounded-xl overflow-hidden">
          {/* v6 F15.3 §1 — Alles open / Alles dicht bar. Alleen zichtbaar bij ≥2 objecten */}
          {objects.length >= 2 && (
            <div className="flex items-center justify-between px-3 py-1.5 bg-[#FAF9F6] border-b border-[#E5E2DC]">
              <span className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#5D7259]">
                {objects.length} objecten
              </span>
              <button
                type="button"
                onClick={toggleAll}
                className="text-[11px] font-semibold text-[#D96C4A] hover:underline"
                data-testid={`joinery-toggle-all-${zone.id}`}
              >
                {allOpen ? "Alles dicht" : "Alles open"}
              </button>
            </div>
          )}
          {objects.map((obj, idx) => (
            <JoineryObjectRow
              key={obj.id}
              obj={obj}
              idx={idx}
              open={openMap[obj.id] !== undefined ? openMap[obj.id] : false}
              onToggleOpen={() => toggleOne(obj.id)}
              allObjects={objects}
              kindDefaults={partsDefaults[obj.kind] || []}
              objectQty={objectQtys[idx]}
              onUpdate={(patch) => updateObject(obj.id, patch)}
              onRemove={() => removeObject(obj.id)}
              zoneType={zone.zone_type}
            />
          ))}
          {/* Totaal — toont m² én lm zodra beide relevant zijn. */}
          <div className="bg-[#FAF9F6] border-t-2 border-[#B07A4F]/40 px-3 py-2 flex items-center justify-between" data-testid={`joinery-total-${zone.id}`}>
            <span className="text-[11px] uppercase tracking-[0.1em] font-bold text-[#5D7259]">
              Totaal schrijnwerk
            </span>
            <strong className="text-sm text-[#2C2C2A]">
              {totalM2.toFixed(2)} m²
              {totalLm > 0 && (
                <span className="ml-3 text-[#5D7259]">
                  · {totalLm.toFixed(2)} lm
                </span>
              )}
            </strong>
          </div>
        </div>
      )}

      {/* ───── 4. Sjabloon + werken (PaintMeasure-stijl checklist) ───── */}
      {objects.length > 0 && (
        <WorksSection
          zone={zone}
          works={works}
          activeByPc={activeByPc}
          activeByLine={activeByLine}
          joineryTemplates={joineryTemplates}
          activeTemplate={activeTemplate}
          activeTemplateId={activeTemplateId}
          templateMissing={templateMissing}
          templateWrongSide={templateWrongSide}
          templateLegacy={templateLegacy}
          legacyTemplate={legacyTemplate}
          defaultSide={defaultSide}
          pcById={pcById}
          zoneQty={zoneQty}
          zoneQtyUnit={zoneQtyUnit}
          zoneSubtotal={zoneSubtotal}
          totalM2={totalM2}
          totalLm={totalLm}
          partsDefaults={partsDefaults}
          autoSources={autoSources}
          onSetTemplate={setTemplate}
          onToggleLine={toggleLine}
          onUpdateWork={updateWork}
        />
      )}
    </div>
  );
}

// ============================================================================
// v6 F16 §3 — Object-picker: één sobere "+ Object toevoegen"-knop.
// Klik opent een modal met logisch gegroepeerde objecten (Deuren & ramen,
// Meubels & kasten, Panelen & overig). Op mobiel als bottom sheet
// (fixed inset-x-0 bottom-0, rounded-top), op desktop centered dialog.
// Sluit automatisch na keuze zodat de gebruiker meteen het object kan
// invullen.
// ============================================================================
function JoineryObjectPicker({ zoneId, zoneType, onAdd }) {
  const [open, setOpen] = useState(false);
  const groups = OBJECT_GROUPS_BY_ZONE_TYPE[zoneType] || OBJECT_GROUPS_BY_ZONE_TYPE.joinery;

  // ESC key sluit modal
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    // Body scroll lock zolang modal open is
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  const handlePick = (kind) => {
    onAdd(kind);
    setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full h-11 rounded-xl border border-dashed border-[#B07A4F]/60 bg-[#FFF8F2] hover:bg-[#FDEFE4] hover:border-[#B07A4F] text-[#7F4124] font-medium flex items-center justify-center gap-2 transition"
        data-testid={`joinery-picker-open-${zoneId}`}
      >
        <Plus className="w-4 h-4" />
        Object toevoegen
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/50"
          onClick={() => setOpen(false)}
          data-testid={`joinery-picker-modal-${zoneId}`}
          role="dialog"
          aria-modal="true"
          aria-label="Object toevoegen"
        >
          <div
            className="bg-white w-full sm:max-w-lg max-h-[85vh] sm:max-h-[80vh] rounded-t-2xl sm:rounded-2xl shadow-xl overflow-hidden flex flex-col animate-in slide-in-from-bottom-4 sm:zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E2DC]">
              <div>
                <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-[#5D7259]">
                  Kies een objecttype
                </div>
                <div className="text-base font-heading font-semibold text-[#2C2C2A] mt-0.5">
                  Object toevoegen
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="w-9 h-9 rounded-lg hover:bg-[#F0EFEA] flex items-center justify-center text-[#4F4F4D]"
                aria-label="Sluiten"
                data-testid={`joinery-picker-close-${zoneId}`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Body — scroll-safe */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
              {groups.map((group) => (
                <div key={group.label}>
                  <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#5D7259] mb-1.5 px-1">
                    {group.label}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {group.kinds.map((kind) => (
                      <button
                        key={kind}
                        type="button"
                        onClick={() => handlePick(kind)}
                        className="h-11 px-3 rounded-lg border border-[#E5E2DC] bg-white hover:border-[#B07A4F] hover:bg-[#FFF8F2] text-sm text-[#2C2C2A] font-medium transition text-left"
                        data-testid={`joinery-add-${kind}`}
                      >
                        {OBJECT_KIND_LABEL[kind] || kind}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Footer — enkel op mobiel voor grote sluitknop */}
            <div className="sm:hidden px-4 py-3 border-t border-[#E5E2DC]">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="w-full h-11 rounded-lg border border-[#E5E2DC] bg-white text-sm font-medium text-[#4F4F4D] hover:bg-[#F0EFEA]"
                data-testid={`joinery-picker-cancel-${zoneId}`}
              >
                Annuleren
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}


// ============================================================================
// Object-kaart — v6 F15.2 §2 herwerkt naar de rustige "Ruimte toevoegen"
// visuele taal. Layout per object:
//   1. Kop: naam-input + prullenbak
//   2. Maatvelden met labels boven (Breedte, Hoogte, Diepte alleen bij kasten)
//   3. Compacte resultaatkaart: "Behandelend oppervlak" + optioneel "· lm"
//   4. Chevron "Mee opnemen in berekening ˅" met parts-editor
// De side-chip (Binnen / Buiten / Beide) is verwijderd — de werkzone
// bepaalt de context, dubbelzijdige onderdelen (deurblad, front) worden
// via parts.sides afgehandeld.
// ============================================================================
function JoineryObjectRow({ obj, idx, open, onToggleOpen, allObjects, kindDefaults = [], objectQty, onUpdate, onRemove, zoneType }) {
  const [partsOpen, setPartsOpen] = useState(false);
  const [sidelightsOpen, setSidelightsOpen] = useState(false);
  const isParentKind = obj.kind === "shutter_box" || obj.kind === "shutter_slats";
  const parentCandidates = allObjects.filter(
    (o) => o.id !== obj.id && (o.kind === "window" || o.kind === "door")
  );
  const hasDepth = KINDS_WITH_DEPTH.has(obj.kind);
  const hasSidelights = obj.kind === "exterior_door";
  const dimLabels = dimLabelsFor(obj.kind);

  const displayArea = objectQty?.area_m2 || 0;
  const displayLength = objectQty?.length_lm || 0;

  const partStates = useMemo(
    () => mergePartStates(obj, kindDefaults),
    [obj, kindDefaults]
  );
  const enabledCount = partStates.filter((p) => p.enabled && p.count > 0).length;
  const totalParts = partStates.length;

  const commitParts = (mutator) => {
    const next = partStates.map((p) => {
      const m = mutator(p) || {};
      return {
        key: p.key,
        enabled: (m.enabled !== undefined) ? !!m.enabled : p.enabled,
        count: (m.count !== undefined) ? +m.count : p.count,
        sides: (m.sides !== undefined) ? +m.sides : p.sides,
      };
    });
    onUpdate({ parts: next });
  };

  const togglePart = (partKey) =>
    commitParts((p) => p.key === partKey ? { enabled: !p.enabled, count: p.enabled ? p.count : Math.max(1, p.count || p.defaultCount || 1) } : {});

  const updatePartCount = (partKey, count) =>
    commitParts((p) => p.key === partKey ? { count: Math.max(0, +count || 0), enabled: (+count || 0) > 0 } : {});

  const updatePartSides = (partKey, sides) =>
    commitParts((p) => p.key === partKey ? { sides: Math.max(1, +sides || 1) } : {});

  // v6 F15.3 §6 — Zijlichten helpers voor Buitendeur.
  const sl = obj.sidelights || { enabled: false, count: 1, width: 0, height: 0 };
  const updateSidelights = (patch) =>
    onUpdate({ sidelights: { ...sl, ...patch } });

  return (
    <div
      className={`${idx > 0 ? "border-t border-[#E5E2DC]" : ""}`}
      data-testid={`joinery-obj-${obj.id}`}
    >
      {/* v6 F15.3 §1 — Ingeklapte kop: chevron + naam + m² (+ lm) + prullenbak */}
      <div className="px-3 sm:px-4 py-3 flex items-center gap-2">
        <button
          type="button"
          onClick={onToggleOpen}
          className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-[#FAF9F6] flex-shrink-0"
          aria-expanded={open}
          data-testid={`joinery-obj-toggle-${obj.id}`}
        >
          <ChevronDown className={`w-4 h-4 text-[#5D7259] transition-transform ${open ? "" : "-rotate-90"}`} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-[#2C2C2A] truncate" data-testid={`joinery-obj-summary-name-${obj.id}`}>
            {obj.name || OBJECT_KIND_LABEL[obj.kind]}
          </div>
          <div className="text-[11px] text-[#5D7259] tabular-nums">
            {(+obj.count || 1) > 1 && (
              <span className="mr-2 font-semibold text-[#B07A4F]" data-testid={`joinery-obj-summary-count-${obj.id}`}>
                {(+obj.count || 1)} stuks
              </span>
            )}
            {(+obj.count || 1) > 1 && <span className="text-[#C9C8C2] mr-2">·</span>}
            {displayArea.toFixed(2)} m²
            {displayLength > 0 && <span className="ml-2">· {displayLength.toFixed(2)} lm</span>}
            <span className="ml-2 text-[#4F4F4D]">· {OBJECT_KIND_LABEL[obj.kind]}</span>
          </div>
        </div>
        <button
          onClick={onRemove}
          className="w-9 h-9 rounded-md hover:bg-red-50 text-[#D9534F] flex items-center justify-center flex-shrink-0"
          data-testid={`joinery-obj-remove-${obj.id}`}
          aria-label="Verwijder object"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {open && (
        <div className="px-3 sm:px-4 pb-4 pt-0 space-y-3">
          {/* Naam-input (bewerkbaar wanneer open) */}
          <Input
            value={obj.name}
            onChange={(e) => onUpdate({ name: e.target.value })}
            onFocus={autoSelectOnFocus}
            className="h-10 rounded-lg text-sm font-medium"
            placeholder={OBJECT_KIND_LABEL[obj.kind]}
            data-testid={`joinery-obj-name-${obj.id}`}
          />

          {/* v6 F21 — Aantal identieke objecten. Tijdens rondloop bij klant kan
              de gebruiker snel aanpassen (bv. 3 identieke binnendeuren).
              Vermenigvuldigt alle onderdelen (deurblad, kader, …). Auto ×
              (enkelzijde/dubbelzijde) blijft daar apart van via parts.sides. */}
          <div className="grid grid-cols-[auto,1fr] items-center gap-2">
            <Label className="text-[11px] uppercase tracking-wider text-[#5D7259] font-semibold">
              Aantal
            </Label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => onUpdate({ count: Math.max(1, (+obj.count || 1) - 1) })}
                className="w-9 h-9 rounded-lg border border-[#E5E2DC] bg-white hover:bg-[#FAF9F6] text-[#4F4F4D] text-lg font-bold flex items-center justify-center flex-shrink-0"
                aria-label="Aantal verminderen"
                data-testid={`joinery-obj-count-dec-${obj.id}`}
              >
                −
              </button>
              <RawNumInput
                value={+obj.count || 1}
                onChange={(v) => onUpdate({ count: Math.max(1, Math.round(+v || 1)) })}
                className="h-9 w-16 rounded-lg font-mono text-sm text-center"
                data-testid={`joinery-obj-count-${obj.id}`}
              />
              <button
                type="button"
                onClick={() => onUpdate({ count: (+obj.count || 1) + 1 })}
                className="w-9 h-9 rounded-lg border border-[#E5E2DC] bg-white hover:bg-[#FAF9F6] text-[#4F4F4D] text-lg font-bold flex items-center justify-center flex-shrink-0"
                aria-label="Aantal verhogen"
                data-testid={`joinery-obj-count-inc-${obj.id}`}
              >
                +
              </button>
              <span className="text-[11px] text-[#5D7259] ml-1">
                {(+obj.count || 1) === 1 ? "identiek object" : "identieke objecten"}
              </span>
            </div>
          </div>

          {/* Maatvelden met semantische labels per kind */}
          <div className={`grid ${hasDepth ? "grid-cols-3" : "grid-cols-2"} gap-2`}>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-[#5D7259] font-semibold">
                {dimLabels.length}
              </Label>
              <RawNumInput
                value={obj.length}
                onChange={(v) => onUpdate({ length: +v || 0 })}
                className="h-11 mt-1 rounded-xl font-mono text-sm"
                data-testid={`joinery-obj-length-${obj.id}`}
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-[#5D7259] font-semibold">
                {dimLabels.height}
              </Label>
              <RawNumInput
                value={obj.height}
                onChange={(v) => onUpdate({ height: +v || 0 })}
                className="h-11 mt-1 rounded-xl font-mono text-sm"
                data-testid={`joinery-obj-height-${obj.id}`}
              />
            </div>
            {hasDepth && (
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-[#5D7259] font-semibold">
                  {dimLabels.depth}
                </Label>
                <RawNumInput
                  value={obj.depth}
                  onChange={(v) => onUpdate({ depth: +v || 0 })}
                  className="h-11 mt-1 rounded-xl font-mono text-sm"
                  data-testid={`joinery-obj-depth-${obj.id}`}
                />
              </div>
            )}
          </div>

          {/* Resultaatkaart */}
          <div
            className="bg-[#FAF9F6] border border-[#E5E2DC] rounded-xl px-3 py-2.5 flex items-center justify-between gap-3"
            data-testid={`joinery-obj-area-${obj.id}`}
          >
            <span className="text-[11px] uppercase tracking-[0.1em] font-bold text-[#5D7259]">
              Behandelend oppervlak
            </span>
            <div className="text-right tabular-nums">
              <div className="text-base font-semibold text-[#2C2C2A]">
                {displayArea.toFixed(2)} m²
              </div>
              {displayLength > 0 && (
                <div className="text-[10px] text-[#5D7259] font-medium leading-none">
                  + {displayLength.toFixed(2)} lm
                </div>
              )}
            </div>
          </div>

          {/* v6 F42k v14f — Segmented control voor meetmethode.
              v14at-P10-A — Twee ONAFHANKELIJK selecteerbare knoppen (aria-pressed
              i.p.v. aria-selected). Beide tegelijk aan = "area_m2+frame_lm";
              beide uit = "none" (object bewust gedeactiveerd, afmetingen bewaard). */}
          {SIMPLE_MODE_KINDS.has(obj.kind) && (() => {
            const _mm = obj.measurement_mode;
            const areaOn = _mm === "area_m2" || _mm === "area_m2+frame_lm";
            const frameOn = _mm === "frame_lm" || _mm === "area_m2+frame_lm";
            const neitherOn = _mm === "none";
            const computeNewMode = (nextArea, nextFrame) => {
              if (nextArea && nextFrame) return "area_m2+frame_lm";
              if (nextArea) return "area_m2";
              if (nextFrame) return "frame_lm";
              return "none";
            };
            return (
              <div
                className="space-y-1.5"
                data-testid={`joinery-mode-seg-${obj.id}`}
              >
                <div
                  className="flex items-center gap-2 bg-white border border-[#E5E2DC] rounded-xl p-1"
                  role="group"
                  aria-label="Meetmethode — Oppervlak en/of Kaderwerk"
                >
                  <button
                    type="button"
                    aria-pressed={areaOn}
                    onClick={() => {
                      const newMode = computeNewMode(!areaOn, frameOn);
                      onUpdate({
                        measurement_mode: newMode,
                        simple_mode: newMode !== "none",
                        window_has_cross_division: false,
                      });
                    }}
                    className={`flex-1 h-9 rounded-lg text-[12px] font-semibold transition ${
                      areaOn
                        ? "bg-[#B07A4F] text-white shadow-sm"
                        : "bg-transparent text-[#5D7259] hover:bg-[#FAF9F6]"
                    }`}
                    data-testid={`joinery-mode-m2-${obj.id}`}
                    title="Behandelbare vlakken (m²) — onafhankelijk aan/uit te zetten"
                  >
                    Oppervlak m²
                  </button>
                  <button
                    type="button"
                    aria-pressed={frameOn}
                    onClick={() => {
                      const newMode = computeNewMode(areaOn, !frameOn);
                      onUpdate({
                        measurement_mode: newMode,
                        simple_mode: newMode !== "none",
                        window_has_cross_division: false,
                        simple_moving_parts: obj.simple_moving_parts ?? (obj.kind.includes("window") ? 2 : 1),
                        simple_fixed_parts: obj.simple_fixed_parts ?? 0,
                      });
                    }}
                    className={`flex-1 h-9 rounded-lg text-[12px] font-semibold transition ${
                      frameOn
                        ? "bg-[#B07A4F] text-white shadow-sm"
                        : "bg-transparent text-[#5D7259] hover:bg-[#FAF9F6]"
                    }`}
                    data-testid={`joinery-mode-lm-${obj.id}`}
                    title="Kaderwerk (lm) — onafhankelijk aan/uit te zetten"
                  >
                    Kaderwerk lm
                  </button>
                </div>
                {neitherOn && (
                  <div
                    className="text-[11px] text-[#7F4124] italic px-2"
                    data-testid={`joinery-mode-inactief-${obj.id}`}
                  >
                    Dit object levert geen oppervlakte of kaderwerk aan de offerte. Afmetingen en instellingen blijven bewaard.
                  </div>
                )}
              </div>
            );
          })()}

          {/* v6 F42k P2 v12 — Vereenvoudigd model: aparte UI per kind-familie.
              RAMEN → constructie in lm (+ kruis-toggle + schuifraam-toggle).
              DEUREN → hout/glas presets voor deurblad + dubbele-deur-toggle.
              Overige simple-kinds → legacy generieke slider.
              v14f-fix: in "Oppervlak m²"-modus zijn constructievelden niet nodig
              → block verbergen om verwarring te voorkomen. */}
          {obj.simple_mode && obj.measurement_mode !== "area_m2" && (
            <div
              className="bg-white border border-[#E5E2DC] rounded-xl p-3 space-y-3"
              data-testid={`joinery-simple-block-${obj.id}`}
            >
              <div className="flex items-center gap-2">
                <span className="text-[11px] uppercase tracking-[0.1em] font-bold text-[#5D7259]">
                  {obj.kind.includes("window")
                    ? (obj.window_sliding ? "Schuifraam — constructie" : "Raam — constructie")
                    : (obj.kind.includes("door") || obj.kind.includes("gate"))
                      ? "Deur — samenstelling"
                      : "Samenstelling"}
                </span>
              </div>

              {/* ─── RAAM UI (v13) ────────────────────────────────
                   • Schuifraam is een APART kind (via + Object) → geen toggle meer.
                   • Standaard alleen B×H tonen; middenstijl on/off.
                   • "Meer opties" collapsible bevat kruis, bovenlicht en ongelijke verdeling.
                   • Zijde-toggle enkel bij exterior_joinery zone. */}
              {obj.kind.includes("window") && (
                <>
                  {/* Kernvraag: middenstijl aanwezig? — enkel wanneer moving≥2 */}
                  {(+obj.simple_moving_parts || 0) >= 2 && !obj.window_has_cross_division && !obj.window_sliding && (
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] text-[#4F4F4D]">Middenstijl aanwezig?</span>
                      <div className="flex gap-1">
                        <button type="button"
                          onClick={() => onUpdate({ window_middle_pillar: true, window_double_movers_no_middle: false })}
                          className={`text-[11px] px-3 py-1 rounded-md border transition ${
                            (obj.window_middle_pillar !== false && !obj.window_double_movers_no_middle)
                              ? "bg-[#B07A4F] text-white border-[#B07A4F]"
                              : "bg-white text-[#4F4F4D] border-[#E5E2DC] hover:bg-[#FAF9F6]"
                          }`}
                          data-testid={`joinery-win-middle-yes-${obj.id}`}>Ja</button>
                        <button type="button"
                          onClick={() => onUpdate({ window_middle_pillar: false, window_double_movers_no_middle: true })}
                          className={`text-[11px] px-3 py-1 rounded-md border transition ${
                            (obj.window_middle_pillar === false || obj.window_double_movers_no_middle)
                              ? "bg-[#B07A4F] text-white border-[#B07A4F]"
                              : "bg-white text-[#4F4F4D] border-[#E5E2DC] hover:bg-[#FAF9F6]"
                          }`}
                          data-testid={`joinery-win-middle-no-${obj.id}`}>Nee</button>
                      </div>
                    </div>
                  )}

                  {/* v13 — Schuifraam-inputs (enkel indien window_sliding=true, bv. via nieuw kind exterior_sliding_window) */}
                  {obj.window_sliding && !obj.window_has_cross_division && (
                    <div className="space-y-2 border-t border-dashed border-[#E5E2DC] pt-2">
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <Label className="text-[11px] uppercase tracking-wider text-[#5D7259] font-semibold">
                            Verdeling vast / schuivend
                          </Label>
                          <span className="text-[11px] font-mono tabular-nums text-[#2C2C2A]" data-testid={`joinery-win-split-value-${obj.id}`}>
                            {Math.round((+obj.window_sliding_split_ratio ?? 0.5) * 100)}% vast · {Math.round((1 - (+obj.window_sliding_split_ratio ?? 0.5)) * 100)}% schuivend
                          </span>
                        </div>
                        <input type="range" min={0} max={100} step={5}
                          value={Math.round((+obj.window_sliding_split_ratio ?? 0.5) * 100)}
                          onChange={(e) => onUpdate({ window_sliding_split_ratio: Math.min(1, Math.max(0, (+e.target.value || 0) / 100)) })}
                          className="w-full accent-[#B07A4F]"
                          data-testid={`joinery-win-split-${obj.id}`} />
                      </div>
                    </div>
                  )}

                  {/* v13 — Kruisverdeling-uitlegblok wanneer actief */}
                  {obj.window_has_cross_division && (
                    <p className="text-[11px] text-[#5D7259] leading-snug bg-[#FFF8ED] border border-[#F2DCB2] rounded-md p-2">
                      Volledig raamoppervlak wordt behandeld: {(+obj.length || 0).toFixed(2)} × {(+obj.height || 0).toFixed(2)} × {(+obj.count || 1)} = <strong>{((+obj.length || 0) * (+obj.height || 0) * (+obj.count || 1)).toFixed(2)} m²</strong>. Bewust géén individuele latten berekenen.
                    </p>
                  )}

                  {/* v13 — Zijde-toggle: alleen bij buitenschrijnwerk-zone. Binnen = geen keuze. */}
                  {zoneType === "exterior_joinery" && (
                    <label className="flex items-center gap-2 text-[11px] text-[#4F4F4D]">
                      <input type="checkbox"
                        checked={obj.window_sides === "both"}
                        onChange={(e) => onUpdate({ window_sides: e.target.checked ? "both" : "outside" })}
                        className="accent-[#B07A4F]"
                        data-testid={`joinery-win-inside-extra-${obj.id}`} />
                      Ook binnenzijde behandelen (buitenzijde blijft altijd actief)
                    </label>
                  )}

                  {/* v13 — Meer opties collapsible */}
                  {!obj.window_sliding && (
                    <details className="border-t border-dashed border-[#E5E2DC] pt-2" data-testid={`joinery-win-more-${obj.id}`}>
                      <summary className="text-[11px] text-[#B07A4F] cursor-pointer select-none hover:underline">
                        Meer opties
                      </summary>
                      <div className="mt-2 space-y-3">
                        {/* Ongelijke verdeling */}
                        {!obj.window_has_cross_division && (
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-[11px] uppercase tracking-wider text-[#5D7259] font-semibold">Vaste delen</Label>
                              <RawNumInput value={obj.simple_fixed_parts || 0}
                                onChange={(v) => onUpdate({ simple_fixed_parts: Math.max(0, Math.round(+v || 0)) })}
                                className="h-10 mt-1 rounded-lg font-mono text-sm text-center"
                                data-testid={`joinery-simple-fixed-${obj.id}`} />
                            </div>
                            <div>
                              <Label className="text-[11px] uppercase tracking-wider text-[#5D7259] font-semibold">Bewegende delen</Label>
                              <RawNumInput value={obj.simple_moving_parts ?? 2}
                                onChange={(v) => onUpdate({ simple_moving_parts: Math.max(0, Math.round(+v || 0)) })}
                                className="h-10 mt-1 rounded-lg font-mono text-sm text-center"
                                data-testid={`joinery-simple-moving-${obj.id}`} />
                            </div>
                          </div>
                        )}
                        {!obj.window_has_cross_division && (+obj.simple_moving_parts || 0) >= 1 && (
                          <div>
                            <Label className="text-[11px] uppercase tracking-wider text-[#5D7259] font-semibold">
                              Breedte bewegende delen (m) — leeg = gelijk verdeeld
                            </Label>
                            <div className="grid grid-cols-3 gap-1.5 mt-1">
                              {Array.from({ length: +obj.simple_moving_parts || 0 }).map((_, i) => (
                                <RawNumInput key={i}
                                  value={(obj.window_moving_widths || [])[i] ?? 0}
                                  onChange={(v) => {
                                    const arr = [...(obj.window_moving_widths || [])];
                                    while (arr.length <= i) arr.push(0);
                                    arr[i] = +v || 0;
                                    onUpdate({ window_moving_widths: arr });
                                  }}
                                  className="h-9 rounded-lg font-mono text-sm text-center"
                                  data-testid={`joinery-win-movewidth-${obj.id}-${i}`} />
                              ))}
                            </div>
                          </div>
                        )}
                        {/* Vast bovenlicht */}
                        {!obj.window_has_cross_division && (
                          <div className="space-y-1.5">
                            <label className="flex items-center gap-2 text-[11px] text-[#4F4F4D]">
                              <input type="checkbox"
                                checked={!!obj.window_has_toplight}
                                onChange={(e) => onUpdate({ window_has_toplight: e.target.checked })}
                                className="accent-[#B07A4F]"
                                data-testid={`joinery-win-toplight-${obj.id}`} />
                              Vaste bovenstrook / bovenlicht
                            </label>
                            {obj.window_has_toplight && (
                              <div className="flex gap-1.5 pl-6">
                                {[
                                  { v: 1/3, l: "1/3 hoogte" },
                                  { v: 1/4, l: "1/4 hoogte" },
                                ].map((o) => (
                                  <button key={o.l} type="button"
                                    onClick={() => onUpdate({ window_toplight_ratio: o.v })}
                                    className={`text-[10px] px-2 py-1 rounded-md border transition ${
                                      Math.abs((+obj.window_toplight_ratio ?? 0.25) - o.v) < 0.01
                                        ? "bg-[#B07A4F] text-white border-[#B07A4F]"
                                        : "bg-white text-[#4F4F4D] border-[#E5E2DC] hover:bg-[#FAF9F6]"
                                    }`}
                                    data-testid={`joinery-win-toplight-preset-${obj.id}-${Math.round(o.v * 100)}`}>{o.l}</button>
                                ))}
                                <RawNumInput
                                  value={obj.window_toplight_ratio ?? 0.25}
                                  onChange={(v) => onUpdate({ window_toplight_ratio: Math.min(1, Math.max(0, +v || 0)) })}
                                  className="h-7 w-16 rounded-md font-mono text-[10px] text-center"
                                  data-testid={`joinery-win-toplight-ratio-${obj.id}`} />
                                <span className="text-[10px] text-[#6E6E6C] self-center">handmatig (0-1)</span>
                              </div>
                            )}
                          </div>
                        )}
                        {/* Kruisverdeling */}
                        <label className="flex items-center gap-2 text-[11px] text-[#4F4F4D]">
                          <input type="checkbox"
                            checked={!!obj.window_has_cross_division}
                            onChange={(e) => onUpdate({ window_has_cross_division: e.target.checked })}
                            className="accent-[#B07A4F]"
                            data-testid={`joinery-win-cross-${obj.id}`} />
                          Kruisverdeling — volledig raamoppervlak in m²
                        </label>
                      </div>
                    </details>
                  )}

                  <p className="text-xs text-[#6E6E6C] leading-snug">
                    EmmaMeasure berekent zelf het te behandelen kaderwerk. Hierboven zie je de resulterende meters live.
                  </p>
                </>
              )}

              {/* ─── DEUR UI ─────────────────────────────────────── */}
              {(obj.kind.includes("door") || obj.kind.includes("gate")) && !obj.kind.includes("window") && (
                <>
                  <div>
                    <div className="flex items-center justify-between mb-1 gap-2">
                      <Label className="text-[11px] uppercase tracking-wider text-[#5D7259] font-semibold">
                        Hout / glas-verhouding (deurblad)
                      </Label>
                      <span className="text-[11px] font-mono tabular-nums text-[#2C2C2A]" data-testid={`joinery-door-ratio-value-${obj.id}`}>
                        {Math.round((obj.simple_wood_ratio ?? 1.0) * 100)}% hout
                      </span>
                    </div>
                    <div className="flex gap-1.5 flex-wrap">
                      {[
                        { pct: 100, label: "Volledig hout" },
                        { pct: 67, label: "2/3 hout" },
                        { pct: 50, label: "1/2 hout" },
                        { pct: 33, label: "1/3 hout" },
                        { pct: 0, label: "Volledig glas" },
                      ].map((p) => (
                        <button
                          key={p.pct}
                          type="button"
                          onClick={() => onUpdate({ simple_wood_ratio: p.pct / 100 })}
                          className={`text-[11px] px-2 py-1.5 rounded-md border transition ${
                            Math.round((obj.simple_wood_ratio ?? 1.0) * 100) === p.pct
                              ? "bg-[#B07A4F] text-white border-[#B07A4F]"
                              : "bg-white text-[#4F4F4D] border-[#E5E2DC] hover:bg-[#FAF9F6]"
                          }`}
                          data-testid={`joinery-door-preset-${obj.id}-${p.pct}`}
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      step={5}
                      value={Math.round((obj.simple_wood_ratio ?? 1.0) * 100)}
                      onChange={(e) => onUpdate({ simple_wood_ratio: Math.min(1, Math.max(0, (+e.target.value || 0) / 100)) })}
                      className="w-full accent-[#B07A4F] mt-2"
                      data-testid={`joinery-door-ratio-${obj.id}`}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => onUpdate({ door_double: !obj.door_double })}
                    className={`text-[11px] px-2 py-1.5 rounded-md border transition ${
                      obj.door_double
                        ? "bg-[#B07A4F] text-white border-[#B07A4F]"
                        : "bg-white text-[#4F4F4D] border-[#E5E2DC] hover:bg-[#FAF9F6]"
                    }`}
                    data-testid={`joinery-door-double-${obj.id}`}
                  >
                    {obj.door_double ? "✓ Dubbele deur" : "Dubbele deur?"}
                  </button>
                  {/* v13 — Zijde-toggle: alleen bij buitenschrijnwerk-zone. Binnen = geen keuze. */}
                  {zoneType === "exterior_joinery" && (
                    <label className="flex items-center gap-2 text-[11px] text-[#4F4F4D]">
                      <input type="checkbox"
                        checked={obj.window_sides === "both"}
                        onChange={(e) => onUpdate({ window_sides: e.target.checked ? "both" : "outside" })}
                        className="accent-[#B07A4F]"
                        data-testid={`joinery-door-inside-extra-${obj.id}`} />
                      Ook binnenzijde behandelen (buitenzijde blijft altijd actief)
                    </label>
                  )}
                  <p className="text-xs text-[#6E6E6C] leading-snug">
                    Deurblad = breedte × hoogte × hout-aandeel. Kader (2H + W) wordt altijd apart berekend en verdwijnt nooit. Voor zijlicht/bovenlicht gebruik je de bestaande sectie hierboven.
                  </p>
                </>
              )}

              {/* ─── OVERIGE simple kinds ────────────────────────── */}
              {!obj.kind.includes("window") && !obj.kind.includes("door") && !obj.kind.includes("gate") && (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[11px] uppercase tracking-wider text-[#5D7259] font-semibold">Vaste delen</Label>
                      <RawNumInput value={obj.simple_fixed_parts || 0}
                        onChange={(v) => onUpdate({ simple_fixed_parts: Math.max(0, Math.round(+v || 0)) })}
                        className="h-10 mt-1 rounded-lg font-mono text-sm text-center"
                        data-testid={`joinery-simple-fixed-${obj.id}`} />
                    </div>
                    <div>
                      <Label className="text-[11px] uppercase tracking-wider text-[#5D7259] font-semibold">Bewegende delen</Label>
                      <RawNumInput value={obj.simple_moving_parts ?? 1}
                        onChange={(v) => onUpdate({ simple_moving_parts: Math.max(0, Math.round(+v || 0)) })}
                        className="h-10 mt-1 rounded-lg font-mono text-sm text-center"
                        data-testid={`joinery-simple-moving-${obj.id}`} />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <Label className="text-[11px] uppercase tracking-wider text-[#5D7259] font-semibold">Hout / glas-verhouding</Label>
                      <span className="text-[11px] font-mono tabular-nums text-[#2C2C2A]" data-testid={`joinery-simple-ratio-value-${obj.id}`}>
                        {Math.round((obj.simple_wood_ratio ?? 0.30) * 100)}% hout
                      </span>
                    </div>
                    <input type="range" min={0} max={100} step={5}
                      value={Math.round((obj.simple_wood_ratio ?? 0.30) * 100)}
                      onChange={(e) => onUpdate({ simple_wood_ratio: Math.min(1, Math.max(0, (+e.target.value || 0) / 100)) })}
                      className="w-full accent-[#B07A4F]"
                      data-testid={`joinery-simple-ratio-${obj.id}`} />
                  </div>
                </>
              )}
            </div>
          )}

          {/* v6 F42k v14f — Mode-toggle is nu de segmented control bovenaan
              het object. Geen aparte terug-knop meer nodig. */}

          {/* Parent link (compact, optioneel — voor rolluikkast/lamellen) */}
          {isParentKind && parentCandidates.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-[#6E6E6C]">⤴ Gekoppeld aan:</span>
              <select
                value={obj.parent_object_id || ""}
                onChange={(e) => onUpdate({ parent_object_id: e.target.value || null })}
                className="h-8 px-2 rounded-md border border-[#E5E2DC] text-[11px] flex-1 max-w-xs"
                data-testid={`joinery-obj-parent-${obj.id}`}
              >
                <option value="">— Geen koppeling —</option>
                {parentCandidates.map((p) => (
                  <option key={p.id} value={p.id}>{p.name || OBJECT_KIND_LABEL[p.kind]}</option>
                ))}
              </select>
            </div>
          )}

          {/* v6 F15.3 §6 — Zijlichten (Buitendeur). Aparte optionele sectie. */}
          {hasSidelights && (
            <div className="border border-[#E5E2DC] rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setSidelightsOpen((v) => !v)}
                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-[#FAF9F6]"
                aria-expanded={sidelightsOpen}
                data-testid={`joinery-sidelights-toggle-${obj.id}`}
              >
                <ChevronDown className={`w-3.5 h-3.5 text-[#5D7259] transition-transform ${sidelightsOpen ? "" : "-rotate-90"}`} />
                <span className="text-[11px] uppercase tracking-[0.1em] font-bold text-[#5D7259]">
                  Zijlicht(en) opnemen
                </span>
                {sl.enabled && (
                  <span className="text-[10px] text-[#B07A4F] font-semibold ml-auto">
                    ✓ Actief
                  </span>
                )}
              </button>
              {sidelightsOpen && (
                <div className="px-3 pb-3 pt-1 space-y-2 border-t border-[#E5E2DC]" data-testid={`joinery-sidelights-body-${obj.id}`}>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => updateSidelights({ enabled: !sl.enabled })}
                      className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition ${
                        sl.enabled ? "bg-[#B07A4F] border-[#B07A4F] text-white" : "border-[#C9C8C2]"
                      }`}
                      aria-checked={!!sl.enabled}
                      role="checkbox"
                      data-testid={`joinery-sidelights-enable-${obj.id}`}
                    >
                      {sl.enabled && <Check className="w-3.5 h-3.5" />}
                    </button>
                    <span className="text-sm text-[#2C2C2A]">Vaste zijlichten meetellen</span>
                  </div>
                  {sl.enabled && (
                    <div className="grid grid-cols-3 gap-2 mt-1">
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-[#5D7259] font-semibold">
                          Aantal
                        </Label>
                        <select
                          value={sl.count || 1}
                          onChange={(e) => updateSidelights({ count: +e.target.value })}
                          className="h-10 mt-1 px-2 rounded-lg border border-[#E5E2DC] text-sm w-full"
                          data-testid={`joinery-sidelights-count-${obj.id}`}
                        >
                          <option value={1}>1</option>
                          <option value={2}>2</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-[#5D7259] font-semibold">
                          Breedte (m)
                        </Label>
                        <RawNumInput
                          value={sl.width || 0}
                          onChange={(v) => updateSidelights({ width: +v || 0 })}
                          className="h-10 mt-1 rounded-lg font-mono text-sm"
                          data-testid={`joinery-sidelights-width-${obj.id}`}
                        />
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-[#5D7259] font-semibold">
                          Hoogte (m)
                        </Label>
                        <RawNumInput
                          value={sl.height || obj.height || 0}
                          onChange={(v) => updateSidelights({ height: +v || 0 })}
                          className="h-10 mt-1 rounded-lg font-mono text-sm"
                          data-testid={`joinery-sidelights-height-${obj.id}`}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Chevron "Mee opnemen in berekening" — enkel in klassieke modus */}
          {!obj.simple_mode && totalParts > 0 && (
            <div className="pt-1">
              <button
                type="button"
                onClick={() => setPartsOpen((v) => !v)}
                className="w-full flex items-center gap-2 text-left px-2 py-1.5 rounded-md hover:bg-[#FAF9F6] transition"
                aria-expanded={partsOpen}
                data-testid={`joinery-parts-toggle-${obj.id}`}
              >
                <ChevronDown
                  className={`w-3.5 h-3.5 text-[#5D7259] transition-transform ${partsOpen ? "" : "-rotate-90"}`}
                />
                <span className="text-[11px] uppercase tracking-[0.1em] font-bold text-[#5D7259]">
                  Mee opnemen in berekening
                </span>
                <span className="text-[11px] text-[#4F4F4D] ml-auto">
                  {enabledCount} van {totalParts} onderdelen
                </span>
              </button>

              {partsOpen && (
                <div
                  className="mt-1.5 border border-[#E5E2DC] rounded-lg divide-y divide-[#F0EFEA] bg-white"
                  data-testid={`joinery-parts-list-${obj.id}`}
                >
                  {partStates.map((p) => (
                    <PartRow
                      key={p.key}
                      obj={obj}
                      part={p}
                      onToggle={() => togglePart(p.key)}
                      onCountChange={(v) => updatePartCount(p.key, v)}
                      onSidesChange={(v) => updatePartSides(p.key, v)}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// v6 F15.1 — Eén part-rij binnen de parts-editor. Toont checkbox + label +
// live qty-preview per onderdeel. Count-input verschijnt alleen wanneer de
// backend-default count ≠ 1 (typisch bij "2 zijkanten" of "leggers").
// ============================================================================
function PartRow({ obj, part, onToggle, onCountChange, onSidesChange }) {
  const kind = obj?.kind || "custom_object";
  const objCount = Math.max(1, +obj?.count || 1);
  const formulas = PART_FORMULA[kind] || {};
  const fn = formulas[part.key];
  const perInstance = fn ? fn(obj) : 0;
  // v6 F21 — Preview vermenigvuldigt met obj.count zodat "3 stuks binnendeur"
  // preview 5,67 m² i.p.v. 1,89 m² toont (consistent met objectkaart & zone-totaal).
  const total = perInstance * (part.count || 0) * (part.sides || 1) * objCount;
  const showCount = part.defaultCount !== 1;
  // v6 F15.3 §5 — Sides-opties komen nu uit PART_SIDES_OPTIONS (kan 2 of 3 opties zijn).
  const sidesOptions = PART_SIDES_OPTIONS[`${kind}::${part.key}`];
  const showSidesToggle = Array.isArray(sidesOptions) && sidesOptions.length > 1;
  const unitLabel = UNIT_LABEL[part.unit] || part.unit;

  return (
    <div
      className={`flex items-center gap-2 sm:gap-3 px-3 py-2 ${part.enabled ? "bg-[#FFF8F2]" : "hover:bg-[#FAF9F6]"}`}
      data-testid={`joinery-part-${obj.id}-${part.key}`}
    >
      {/* Checkbox */}
      <button
        type="button"
        onClick={onToggle}
        className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition ${
          part.enabled
            ? "bg-[#B07A4F] border-[#B07A4F] text-white"
            : "border-[#C9C8C2] hover:border-[#B07A4F]"
        }`}
        aria-checked={part.enabled}
        role="checkbox"
        data-testid={`joinery-part-toggle-${obj.id}-${part.key}`}
      >
        {part.enabled && <Check className="w-3.5 h-3.5" />}
      </button>

      {/* Label + sides-hint */}
      <div className="flex-1 min-w-0">
        <div className="text-sm text-[#2C2C2A] truncate">{part.label}</div>
        {part.sides > 1 && !showSidesToggle && (
          <div className="text-[10px] text-[#5D7259]">× {part.sides} zijden</div>
        )}
      </div>

      {/* Sides-toggle (compact 1z / 2z / 4z) — dynamische opties per kind × part */}
      {showSidesToggle && (
        <div
          className="inline-flex rounded-md overflow-hidden border border-[#E5E2DC] flex-shrink-0"
          role="radiogroup"
          aria-label="Aantal zijden"
        >
          {sidesOptions.map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => onSidesChange(n)}
              className={`h-8 w-8 text-[11px] font-bold transition ${
                (part.sides || 1) === n
                  ? "bg-[#B07A4F] text-white"
                  : "bg-white text-[#4F4F4D] hover:bg-[#FAF9F6]"
              }`}
              aria-pressed={(part.sides || 1) === n}
              data-testid={`joinery-part-sides-${obj.id}-${part.key}-${n}`}
            >
              {n}z
            </button>
          ))}
        </div>
      )}

      {/* Count (compact) */}
      {showCount && (
        <RawNumInput
          value={part.count}
          onChange={(v) => onCountChange(v)}
          className="w-12 h-8 rounded-md text-center text-sm flex-shrink-0"
          aria-label="Aantal"
          data-testid={`joinery-part-count-${obj.id}-${part.key}`}
        />
      )}

      {/* Preview qty */}
      <div className="w-20 sm:w-24 text-right tabular-nums flex-shrink-0">
        <div
          className={`text-sm font-semibold ${part.enabled && total > 0 ? "text-[#2C2C2A]" : "text-[#A8A7A0]"}`}
          data-testid={`joinery-part-qty-${obj.id}-${part.key}`}
        >
          {total > 0 ? `${total.toFixed(2)} ${unitLabel}` : `— ${unitLabel}`}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Werken-sectie (PaintMeasure-stijl: template → toggleable cards)
// ============================================================================
function WorksSection({
  zone, works, activeByPc, activeByLine, joineryTemplates, activeTemplate, activeTemplateId,
  templateMissing, templateWrongSide, templateLegacy, legacyTemplate,
  defaultSide, pcById, zoneQty, zoneQtyUnit, zoneSubtotal,
  totalM2 = 0, totalLm = 0,
  partsDefaults = {}, autoSources = {},
  onSetTemplate, onToggleLine, onUpdateWork,
}) {
  // Lines die NIET in het sjabloon staan maar wel actief zijn (custom toegevoegd)
  // v6 F22 — Filter op template_line_id: extra werken zijn works zonder link.
  const tplLineIds = new Set(
    (activeTemplate?.template_lines || []).map((l) => l.id).filter(Boolean)
  );
  const extraWorks = works.filter((w) => !w.template_line_id || !tplLineIds.has(w.template_line_id));

  const sideLabel = defaultSide === "interior" ? "Binnen"
                  : defaultSide === "exterior" ? "Buiten"
                  : "Binnen + Buiten";

  return (
    <div
      className="border-2 border-[#B07A4F]/30 rounded-xl p-3 bg-[#FFF8F2]/40 space-y-3"
      data-testid={`joinery-works-${zone.id}`}
    >
      {/* Sjabloon-picker — strikt DB-gevoed, geen hardcoded fallbacks */}
      <div>
        <div className="text-[11px] uppercase tracking-[0.1em] font-bold text-[#5D7259] mb-2 flex items-center justify-between">
          <span>Schrijnwerk-sjabloon</span>
          <span className="text-[10px] normal-case tracking-normal text-[#4F4F4D]">
            Filter: <strong className="text-[#B07A4F]">{sideLabel}</strong>
          </span>
        </div>
        <select
          value={(templateMissing || templateWrongSide || templateLegacy) ? "" : activeTemplateId}
          onChange={(e) => {
            const newId = e.target.value;
            // v6 F16.2 — Bij migratie van een legacy sjabloon naar een nieuw sjabloon,
            // vraag bevestiging voor het vervangen van oude sjabloonregels.
            if (templateLegacy && newId && newId !== activeTemplateId) {
              const ok = window.confirm(
                "Oude sjabloonregels vervangen door nieuw sjabloon?\n\n" +
                "De verouderde koppeling wordt vervangen. Extra werken die je zelf hebt toegevoegd blijven behouden."
              );
              if (!ok) return;
            }
            onSetTemplate(newId);
          }}
          className={`h-10 w-full rounded-lg border bg-white px-3 text-sm ${templateMissing ? "border-[#D9534F] text-[#D9534F]" : templateLegacy ? "border-[#E4B16D] text-[#7F4124] bg-[#FFF8ED]" : templateWrongSide ? "border-[#E4B16D] text-[#7F4124]" : "border-[#E5E2DC]"}`}
          data-testid={`joinery-template-${zone.id}`}
        >
          <option value="">
            {templateLegacy ? `⚠ Verouderd sjabloon — kies een nieuw actief sjabloon`
              : templateMissing ? "⚠ Template niet gevonden — kies een nieuwe"
              : templateWrongSide ? "⚠ Vorige sjabloon past niet bij deze zijde — kies een nieuwe"
              : "— Kies een sjabloon —"}
          </option>
          {joineryTemplates.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
        {templateLegacy && (
          <div
            className="text-[11px] text-[#7F4124] mt-1.5 font-medium"
            data-testid={`joinery-template-legacy-${zone.id}`}
          >
            Dit sjabloon is verouderd ({legacyTemplate?.name}). Kies een nieuw actief sjabloon uit de lijst hierboven. De oude sjabloonregels worden vervangen.
          </div>
        )}
        {templateMissing && (
          <div
            className="text-[11px] text-[#D9534F] mt-1.5 font-medium"
            data-testid={`joinery-template-missing-${zone.id}`}
          >
            ⚠ De vorige sjabloon-koppeling ({activeTemplateId.slice(0, 8)}…) bestaat niet meer in de bibliotheek. Kies een nieuwe sjabloon.
          </div>
        )}
        {templateWrongSide && (
          <div
            className="text-[11px] text-[#7F4124] mt-1.5 font-medium"
            data-testid={`joinery-template-wrongside-${zone.id}`}
          >
            ⚠ De vorige sjabloon past niet bij de huidige zijde ({sideLabel}). Kies een nieuwe of wijzig de uitvoering.
          </div>
        )}
        {!templateMissing && !templateWrongSide && !templateLegacy && activeTemplate?.description && (
          <div className="text-[11px] text-[#5D7259] mt-1.5 italic">
            {activeTemplate.description}
          </div>
        )}
      </div>

      {/* Header: "Selecteer de werken" + zone-subtotaal */}
      <div className="flex items-center justify-between border-t border-[#E5E2DC] pt-3">
        <div className="text-[11px] uppercase tracking-[0.1em] font-bold text-[#5D7259]">
          Selecteer de werken
        </div>
        <div className="text-sm">
          <span className="text-[#4F4F4D]">Zone-totaal:</span>{" "}
          <strong className="text-[#2C2C2A]" data-testid={`joinery-zone-subtotal-${zone.id}`}>
            {fmtEUR(zoneSubtotal)}
          </strong>
        </div>
      </div>

      {!activeTemplate && !templateMissing && !templateWrongSide && (
        <div className="text-sm text-[#4F4F4D] italic py-3 text-center border border-dashed border-[#E5E2DC] rounded-lg">
          Kies eerst een sjabloon om de werken te zien.
        </div>
      )}

      {/* Template lines → toggleable cards (identiek aan PaintMeasure renderActionRow)
          v6 F24 — Relevance-filter: verberg regels met auto_source waarvan
          qty=0 (bv. geen binnendeuren in de zone → binnendeur-regels weg).
          Regels zonder auto_source (forfait Beschermen, manueel) blijven
          altijd zichtbaar. Regel die al aangevinkt is, blijft óók zichtbaar
          zodat gebruiker hem kan uitvinken.
          v14f — pc.unit filter: als geen enkel object in de zone m²-output
          produceert, verbergen we m²-regels; idem voor lm. Algemene items
          (fixed/hour/piece) blijven altijd zichtbaar. */}
      {activeTemplate && (() => {
        const templateLines = activeTemplate.template_lines || [];
        const relevantLines = templateLines.filter((line) => {
          const pc = pcById[line.price_code_id];
          // v14f — pc.unit gate op basis van zone-wide totalen. Regels
          // die al actief zijn blijven zichtbaar (uit-vinkbaar).
          if (pc && !activeByLine[line.id]) {
            if (pc.unit === "m2" && totalM2 <= 0) return false;
            if (pc.unit === "lm" && totalLm <= 0) return false;
          }
          if (!line.auto_source) return true;                        // forfait/manueel
          if (activeByLine[line.id]) return true;                    // reeds actief
          const resolved = resolveAutoSource(
            line.auto_source, zone, autoSources || {}, partsDefaults || {}
          );
          const mult = +line.auto_source_multiplier || 1;
          return (resolved * mult) > 0;
        });
        if (relevantLines.length === 0) {
          return (
            <div className="text-sm text-[#4F4F4D] italic py-3 text-center border border-dashed border-[#E5E2DC] rounded-lg">
              Voeg objecten toe om werkzaamheden te zien.
            </div>
          );
        }
        return (
          <div className="border border-[#E5E2DC] rounded-xl overflow-hidden divide-y divide-[#F0EFEA] bg-white">
            {relevantLines.map((line) => (
              <WorkLineCard
                key={line.id || line.price_code_id}
                line={line}
                pc={pcById[line.price_code_id]}
                activeWork={activeByLine[line.id] || null}
                zone={zone}
                zoneQty={zoneQty}
                zoneQtyUnit={zoneQtyUnit}
                partsDefaults={partsDefaults}
                autoSources={autoSources}
                zoneId={zone.id}
                onToggle={() => onToggleLine(line)}
                onUpdateWork={onUpdateWork}
              />
            ))}
          </div>
        );
      })()}

      {/* Extra (custom) werken — niet in actieve template */}
      {extraWorks.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#6E6E6C] font-bold mb-1.5">
            Extra werken (buiten sjabloon)
          </div>
          <div className="border border-[#E5E2DC] rounded-xl overflow-hidden divide-y divide-[#F0EFEA] bg-white">
            {extraWorks.map((w) => {
              const pc = pcById[w.price_code_id];
              const fakeLine = {
                id: w.id,
                price_code_id: w.price_code_id,
                description_override: w.description,
                unit_price_override: w.unit_price,
                auto_source: w.auto_source || null,
                auto_source_multiplier: w.auto_source_multiplier || null,
                _activeWorkId: w.id,
              };
              return (
                <WorkLineCard
                  key={w.id}
                  line={fakeLine}
                  pc={pc}
                  activeWork={w}
                  zone={zone}
                  zoneQty={zoneQty}
                  zoneQtyUnit={zoneQtyUnit}
                  partsDefaults={partsDefaults}
                  autoSources={autoSources}
                  zoneId={zone.id}
                  onToggle={() => onToggleLine(fakeLine)}
                  onUpdateWork={onUpdateWork}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Eén werk-rij (toggleable card) — visueel identiek aan PaintMeasure action-row
// ============================================================================
function WorkLineCard({
  line, pc, activeWork, zone, zoneQty, zoneQtyUnit, partsDefaults, autoSources, zoneId,
  onToggle, onUpdateWork,
}) {
  const checked = !!activeWork;
  // v6 F21.1 — Testid gebruikt line.id (uniek per template-regel) i.p.v.
  // price_code_id, omdat sjablonen twee regels met dezelfde price_code kunnen
  // bevatten (enkelzijde vs dubbelzijde via auto_source_multiplier).
  const lineKey = line.id || line.price_code_id;
  // v6 F20 — Unit + qty PER regel (fix mobile bug: eerder werd voor alle
  // regels 'm²' + totale zone-m² getoond, ook voor lm-kaders → 1,89 m²
  // i.p.v. 6,00 lm). Regel bepaalt eenheid via pc.unit; qty via auto_source
  // resolver (met optionele multiplier), met fallback op zoneQty.
  // v6 F42k v14d — Regel-qty aligneren met calculator.js / backend v15-tak.
  // Voorheen: `qty = (lineUnit === zoneQtyUnit) ? zoneQty : 0` met zoneQtyUnit
  // hardcoded "m2" → lm/piece custom regels toonden 0 terwijl backend/PDF
  // wél factureerden. Nu volgt de UI-rij dezelfde precedence als calc:
  //   forfait → qty=1
  //   auto_source → resolveAutoSource × multiplier
  //   anders → aggregate over zone-objecten per pc.unit (m2/lm/piece)
  const lineUnit = pc?.unit || zoneQtyUnit || "m2";
  const unitLabel = UNIT_LABEL[lineUnit] || lineUnit;
  const autoMult = +line.auto_source_multiplier || 1;
  let qty = 0;
  if (line.auto_source && zone && partsDefaults) {
    const resolved = resolveAutoSource(line.auto_source, zone, autoSources || {}, partsDefaults);
    if (resolved != null) qty = resolved * autoMult;
  } else if (!line.auto_source && lineUnit === "fixed") {
    // v6 F23 — Forfait/fixed price_codes zonder auto_source → qty=1 forfait.
    qty = 1;
  } else if (zone) {
    // v14d: aggregate over zone-objecten (spiegel van calculator.js r295-309).
    let totArea = 0, totLm = 0, totPieces = 0;
    for (const obj of zone.joinery_objects || []) {
      const q = computeObjectQty(obj, partsDefaults?.[obj.kind] || []);
      totArea += q.area_m2 || 0;
      totLm += q.length_lm || 0;
      totPieces += q.pieces || 0;
    }
    if (lineUnit === "m2") qty = totArea;
    else if (lineUnit === "lm" || lineUnit === "m") qty = totLm;
    else if (lineUnit === "piece") qty = totPieces;
    else qty = 0;
  }
  const description =
    activeWork?.description_override
    || activeWork?.description_snapshot
    || activeWork?.description
    || line.description_override
    || line.description_snapshot
    || pc?.description
    || "—";
  const code = pc?.code || "—";
  const workCategory = pc?.work_category || "—";

  // v6 F42k v14d — Effectieve unit_price met snapshot-fallback (spiegel van
  // calculator.js r287-290 en server.py r4500-4505):
  //   user activeWork.unit_price (!=0) → activeWork.unit_price_snapshot →
  //   line.unit_price_override → pc.default_price
  // We houden twee waarden apart:
  //   * unitPrice — voor calc/lineTotal (met fallback)
  //   * rawUserPrice — voor de <input> waarde, zodat 0 bewerkbaar blijft
  //     en de gebruiker de fallback niet stil overschrijft.
  let unitPrice;
  let rawUserPrice = 0;
  if (checked && activeWork) {
    const up = +activeWork.unit_price;
    rawUserPrice = Number.isFinite(up) ? up : 0;
    if (Number.isFinite(up) && up > 0) {
      unitPrice = up;
    } else if (activeWork.unit_price_snapshot != null) {
      unitPrice = +activeWork.unit_price_snapshot;
    } else if (line.unit_price_override != null) {
      unitPrice = +line.unit_price_override;
    } else {
      unitPrice = pc ? +pc.default_price : 0;
    }
  } else {
    unitPrice = line.unit_price_override != null ? +line.unit_price_override : (pc ? +pc.default_price : 0);
    rawUserPrice = unitPrice;
  }
  const fallbackHint = (checked && Number.isFinite(+activeWork?.unit_price) && +activeWork?.unit_price === 0);

  // v6 F20 — qty is nu al per-regel bovenaan gezet (auto_source + multiplier
  // of fallback op zoneQty). Legacy `zoneQty` blijft alleen als fallback wanneer
  // de regel geen auto_source heeft en de units matchen.
  const lineTotal = qty * unitPrice;

  return (
    <div
      className={`flex items-center gap-3 px-3 py-2.5 ${checked ? "bg-[#FFF8F2]" : "hover:bg-[#FAF9F6]"}`}
      data-testid={`joinery-work-row-${zoneId}-${lineKey}`}
    >
      {/* Toggle */}
      <button
        type="button"
        onClick={onToggle}
        className={`w-7 h-7 rounded-lg border-2 flex items-center justify-center flex-shrink-0 transition ${
          checked
            ? "bg-[#B07A4F] border-[#B07A4F] text-white"
            : "border-[#C9C8C2] hover:border-[#B07A4F]"
        }`}
        data-testid={`joinery-work-toggle-${zoneId}-${lineKey}`}
        aria-label={checked ? "Werk verwijderen" : "Werk toevoegen"}
      >
        {checked && <Check className="w-4 h-4" />}
      </button>

      {/* Description + code — v6 F42k v14b: editable-description per zone-werk.
          User kan tekst aanpassen (override) of resetten naar standaard. */}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-[#2C2C2A]">
          {checked && activeWork ? (
            <EditableDescription
              value={description}
              override={activeWork.description_override}
              defaultValue={activeWork?.description_snapshot || pc?.description || line.description_snapshot || line.description_override || "—"}
              onOverride={(text) => onUpdateWork?.(activeWork.id, { description_override: text })}
              onReset={() => onUpdateWork?.(activeWork.id, { description_override: null })}
              testId={`joinery-work-desc-${zoneId}-${lineKey}`}
            />
          ) : (
            <span className="line-clamp-2 sm:truncate">{description}</span>
          )}
        </div>
        <div className="text-[10px] text-[#5D7259] font-mono truncate">
          {code} · {workCategory}{autoMult > 1 ? ` · ×${autoMult}` : ""}
        </div>
        {/* v6 F36 — Mobiele prijs-samenvatting per regel (sm:hidden).
            v14d: inclusief prijs-editor (was `hidden sm:flex` op de hoofdrij
            → drukte de omschrijving dicht). Nu staat de input onder de
            omschrijving, met de fallback-hint als placeholder. */}
        <div
          className="sm:hidden text-[11px] font-mono text-[#2C2C2A] mt-1 flex items-center gap-1 flex-wrap"
          data-testid={`joinery-work-summary-mobile-${zoneId}-${lineKey}`}
        >
          {lineUnit === "fixed"
            ? <span>1 forfait ×</span>
            : <span>{qty.toLocaleString("nl-BE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {unitLabel} ×</span>}
          {checked ? (
            <span className="inline-flex items-center gap-0.5">
              <span className="text-[#4F4F4D]">€</span>
              <RawNumInput
                value={rawUserPrice}
                onChange={(v) => onUpdateWork(activeWork.id, { unit_price: +v || 0 })}
                className="w-14 h-6 rounded-md text-right text-xs"
                placeholder={fallbackHint ? unitPrice.toLocaleString("nl-BE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : undefined}
                data-testid={`joinery-work-price-mobile-${zoneId}-${lineKey}`}
              />
              {lineUnit !== "fixed" && <span className="text-[10px] text-[#5D7259]">/{unitLabel}</span>}
              {fallbackHint && (
                <span className="text-[9px] text-[#B07A4F] font-semibold ml-1">
                  (fallback {fmtEUR(unitPrice)})
                </span>
              )}
            </span>
          ) : (
            <span>{fmtEUR(unitPrice)}{lineUnit !== "fixed" ? `/${unitLabel}` : ""}</span>
          )}
        </div>
      </div>

      {/* Unit + price (sm+) */}
      <div className="text-[11px] text-[#4F4F4D] hidden sm:block w-12 text-right">{unitLabel}</div>
      <div className="hidden sm:flex items-center gap-1 w-24 justify-end">
        {checked ? (
          <>
            <span className="text-[11px] text-[#4F4F4D]">€</span>
            <RawNumInput
              value={rawUserPrice}
              onChange={(v) => onUpdateWork(activeWork.id, { unit_price: +v || 0 })}
              className="w-16 h-8 rounded-md text-right text-sm"
              placeholder={fallbackHint ? unitPrice.toLocaleString("nl-BE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : undefined}
              data-testid={`joinery-work-price-${zoneId}-${lineKey}`}
            />
          </>
        ) : (
          <span className="text-[11px] text-[#4F4F4D]">€ {unitPrice.toFixed(2)}/{unitLabel}</span>
        )}
      </div>

      {/* Qty — v14d: w-20 op desktop, w-14 op mobile zodat omschrijving max 2 regels blijft */}
      <div className="w-14 sm:w-20 text-right">
        <div
          className="text-sm font-bold text-[#2C2C2A]"
          data-testid={`joinery-work-qty-${zoneId}-${lineKey}`}
        >
          <span className="hidden sm:inline">{qty.toLocaleString("nl-BE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {unitLabel}</span>
          <span className="sm:hidden">{qty.toLocaleString("nl-BE", { maximumFractionDigits: 2 })}{unitLabel === "fixed" ? "" : ` ${unitLabel}`}</span>
        </div>
        {/* v6 F36.1 — Totaalprijs onder qty op mobile (sm:hidden). */}
        <div
          className={`sm:hidden text-sm font-bold mt-0.5 ${checked ? "text-[#B07A4F]" : "text-[#A8A7A0]"}`}
          data-testid={`joinery-work-total-mobile-${zoneId}-${lineKey}`}
        >
          {fmtEUR(checked ? lineTotal : 0)}
        </div>
      </div>

      {/* Line total */}
      <div className="w-24 text-right hidden sm:block">
        <div
          className={`text-sm font-bold ${checked ? "text-[#B07A4F]" : "text-[#A8A7A0]"}`}
          data-testid={`joinery-work-total-${zoneId}-${lineKey}`}
        >
          {fmtEUR(checked ? lineTotal : 0)}
        </div>
      </div>
    </div>
  );
}
